import React, { Suspense, useContext, useState, useRef, useEffect } from 'react';
import { Switch, Route, useHistory, Router, useLocation } from "react-router-dom";
import Loader from 'layout/loader/Loader.js';
import { AppConfig, ToDateString, NewWindow, AddEvent_FUNCTION } from 'library/Library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay, faUserCircle, faLock, faInfoCircle, faSignOutAlt, faArrowUpRightFromSquare, faHeadset, faShield, faPhone, faExchange, faShuffle, faCircleInfo, faRightFromBracket, faUserTie, faEnvelope, faKeyboard, faQrcode, faFileSignature, faLanguage, faStickyNote} from '@fortawesome/free-solid-svg-icons';
import { AppContext } from 'App';
import useModalStack, { ModalPopUp } from 'hooks/useModalStack';
import ChangePassword from 'layout/topbar/component/ChangePassword';
import { LogoutUser, LogoutUserAlert } from 'modules/login/Login';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import PropertySwitch from './layout/topbar/component/PropertySwitch.js';
import { Menu, MenuItem, MenuDivider, MenuHeader, SubMenu } from '@szhsin/react-menu'
import QRCode from 'react-qr-code';
import Attachments, { ShowAttachments } from "layout/attachments/Attachments";
import { UploadSignature } from 'layout/topbar/component/UploadSignature.js';
import useLoader from "hooks/useLoader";
import { ApiRequest } from "library/APICall";
import { ResponseSnackbar, ShowSnackbar } from "layout/snackbar/Snackbar";
import { useTranslation } from 'providers/LocalizationContext.js';



//  SAMPLES
const ContextMenuDemo = React.lazy(() => import('modules/demo/ContextMenuDemo'));
const DummyApiRequest = React.lazy(() => import('modules/demo/DummyApiRequest'));
const HorizontalTabsDemo = React.lazy(() => import('modules/demo/HorizontalTabsDemo'));
const InternalComponentAccessDemo = React.lazy(() => import('modules/demo/InternalComponentAccessDemo/ComponentOne'));
const ModalStackDemo = React.lazy(() => import('modules/demo/ModalStack/ModalStackDemo'));
const Report = React.lazy(() => import('modules/demo/Template/Report/ReportLayout'));
const Template = React.lazy(() => import('modules/demo/Template/Template'));
const VerticalTabsDemo = React.lazy(() => import('modules/demo/VerticalTabsDemo'));


//  APPLICATION SETUP
const AreaTypeSetup = React.lazy(() => import('modules/app/setup/area_type_Setup/AreaTypeSetup.js'));
const CardTypeSetup = React.lazy(() => import('modules/app/setup/card_type_setup/CardTypeSetup.js'));
const CompanyTypeSetup = React.lazy(() => import('modules/app/setup/company_type_setup/CompanyTypeSetup.js'));
const CostCenterSetup = React.lazy(() => import('modules/app/setup/cost_center_setup/CostCenterSetup.js'));
const CurrencySetup = React.lazy(() => import('modules/app/setup/currency_setup/CurrencySetup.js'));
const DepartmentSetup = React.lazy(() => import('modules/app/setup/department_setup/DepartmentSetup.js'));
const DesignationSetup = React.lazy(() => import('modules/app/setup/designation_setup/DesignationSetup.js'));
const EmailTemplateSetup = React.lazy(() => import('modules/app/setup/email_template_setup/Page.js'));
const ExchangeRateSetup = React.lazy(() => import('modules/app/setup/exchange_rate_setup/Page.js'));
const FileTypeSetup = React.lazy(() => import('modules/app/setup/file_type_setup/FileTypeSetup.js'));
const ModuleSettlementSetup = React.lazy(() => import('modules/app/setup/module_settlement_setup/ModuleSettlementSetup.js'));
const OccupationSetup = React.lazy(() => import('modules/app/setup/occupation_setup/OccupationSetup.js'));
const PayableProfileSetup = React.lazy(() => import('modules/app/setup/payable_profile_setup/PayableProfileSetup.js'));
const PaymentModeSetup = React.lazy(() => import('modules/app/setup/payment_mode_setup/PaymentModeSetup.js'));
const PaymentTermSetup = React.lazy(() => import('modules/app/setup/payment_term_setup/PaymentTermSetup.js'));
const PropertySetup = React.lazy(() => import('modules/app/setup/property_setup/PropertySetup.js'));
const ReasonsSetup = React.lazy(() => import('modules/app/setup/reasons_setup/ReasonsSetup.js'));
const ReceivableProfileSetup = React.lazy(() => import('modules/app/setup/receivable_profile_setup/ReceivableProfileSetup.js'));
const ReceivableProfileSubSectionSetup = React.lazy(() => import('modules/app/setup/receivable_profile_sub_section/ReceivableProfileSubSection.js'));
const Roles = React.lazy(() => import('modules/app/setup/roles/Roles.js'));
const SalesExecutiveSetup = React.lazy(() => import('modules/app/setup/sales_executive_setup/SalesExecutiveSetup.js'));
const TaxCodeSetup = React.lazy(() => import('modules/app/setup/tax_code_setup/TaxCodeSetup.js'));
const TaxStructureSetup = React.lazy(() => import('modules/app/setup/tax_structure_setup/TaxStructure.js'));
const User = React.lazy(() => import('modules/app/setup/users/User.js'));
const AutoReportSetup = React.lazy(() => import('modules/app/setup/auto_report_setup/AutoReportSetup.js'));
const APPMemberGroup = React.lazy(() => import('modules/app/setup/membership_group_setup/Page.js'));
const APPMembership = React.lazy(() => import('modules/app/setup/membership_setup/Page.js'));
const APPGuestStatusSetup= React.lazy(() => import('modules/app/setup/guest_status/Page.js'));
const APPVoucherCodeSetup = React.lazy(() => import('modules/app/setup/voucher_code/Page.js'));
const APPGatePassItemSetup = React.lazy(() => import('modules/app/setup/gate_pass_item/GatePassItemSetup.js'));
const AttendantSetup = React.lazy(() => import('modules/app/setup/attendant_setup/Page.js'));
const LoyaltyLink = React.lazy(() => import('modules/app/setup/loyalty_link/Page.js'));
//const AppUserRight = React.lazy(() => import('modules/app/setup/user_rights/Page.js'));
const BillingInstructionSetup = React.lazy(() => import('modules/app/setup/billing_instruction_setup/BillingInstructionSetup.js'));
const BusinessSourceSetup = React.lazy(() => import('modules/app/setup/business_source_setup/BusinessSourceSetup.js'));
const MarketSegmentSource = React.lazy(() => import('modules/app/setup/market_segment_setup/MarketSegmentSetup.js'));
const IPSecurity = React.lazy(() => import('modules/app/setup/ip_security/Page.js'));
// const TransportationMode = React.lazy(() => import('modules/app/setup/transportation_mode_setup/TransportationMode.js'));
// const DriverSetup = React.lazy(() => import('modules/app/setup/driver_setup/DriverSetup.js'));
// const TransportationPurpose = React.lazy(() => import('modules/app/setup/transport_purpose_setup/TransportPurpose.js'));
// const TransportationStation = React.lazy(() => import('modules/app/setup/transport_station_setup/TransportStation.js'));
const AppUserRights = React.lazy(() => import('modules/app/setup/user_right/UserRights.js'));
// const EngineeringItems = React.lazy(() => import('modules/app/setup/engineering_item_setup/EngineeringItemPage.js'));
const AttendanceLink = React.lazy(() => import('modules/app/setup/attendance_link/Page.js'));
const IdentityTypeSetup = React.lazy(() => import('modules/app/setup/identity_type_setup/IdentityTypeSetup.js'));
const KeyManager = React.lazy(() => import('modules/app/setup/key_manager/Page.js'));
const MembershipProperty = React.lazy(() => import('modules/app/setup/membership_property_setup/Page.js'));
const MembershipTier = React.lazy(() => import('modules/app/setup/membership_tier_setup/Page.js'));
const MembershipPointConfiguration = React.lazy(() => import('modules/app/setup/membership_point_configuration/Page.js'));
const IncidentType = React.lazy(() => import('modules/app/setup/incident_type/Page.js'));
const BarterEntry = React.lazy(() => import('modules/app/tools/barter_setup/Page.js'));
const MasterImport = React.lazy(() => import('modules/app/tools/master_import/Page.js'));
const ClientManagement = React.lazy(() => import('modules/app/setup/client_management/Page.js'));
const ProductName = React.lazy(() => import('modules/app/setup/product_name_setup/Page.js'));
const AppSettings = React.lazy(() => import('modules/app/setup/app_settings/APPSettings.js'));
const SeedData = React.lazy(() => import('modules/app/tools/seed_data/Page.js'));
const SMSTemplate= React.lazy(() => import('modules/app/setup/sms_template/Page.js'));
const TaxSlabSetup = React.lazy(() => import('modules/app/setup/tax_slab_setup/TaxSlabSetup.js'));

const BranchSetup = React.lazy(() => import('modules/app/setup/branch_setup/BranchSetup.js'))
const BranchLinkingSetup = React.lazy(() => import('modules/app/setup/branch_linking_setup/BranchLinkingSetup.js'));


//  APPLICATION TOOLS
const DatabaseBackup = React.lazy(() => import('modules/app/tools/backup/Backup.js'));
const DayEndProcess = React.lazy(() => import('modules/app/tools/day_end_process/DayEndProcess.js'));
const MembersPointManagement = React.lazy(() => import('modules/app/tools/members_point_management/MembersPointManagement.js'));
const GatePassIssue =React.lazy(() => import('modules/app/tools/gate_pass_issue/GatePassIssue.js'));
const GatePassReturn =React.lazy(() => import('modules/app/tools/gate_pass_return/GatePassReturn.js'));
const OTPManagement = React.lazy(() => import('modules/app/tools/otp_management/OTPManagement.js'));
const Message = React.lazy(() => import('modules/app/tools/message/Page.js'));
const Complaint = React.lazy(() => import('modules/app/tools/complaint/Page.js'));
//const TransportationSchedule = React.lazy(() => import('modules/app/tools/transportation_schedule/Page.js'));
const ShiftClose = React.lazy(() => import('modules/app/tools/shift_close/Page.js'));
const BillHeaderUpdate = React.lazy(() => import('modules/app/tools/bill_header_update/BillHeaderUpdate.js'));
const IncidentReporting = React.lazy(() => import('modules/app/tools/incident_reporting/Page.js'));

const AnnualMaintenanceCost = React.lazy(() => import('modules/app/tools/annual_maintenance_cost/Page.js'));
const SuperUser = React.lazy(() => import('modules/app/tools/super_user/Page.js'));
const SupportTool = React.lazy(() => import('modules/app/tools/support_tool/SupportTool.js'));
const Localization = React.lazy(() => import('modules/app/tools/localization/Page.js'));
const LocalizationResponse = React.lazy(() => import('modules/app/tools/localization_response/Page.js'));

//  APPLICATION REPORT
const APPActivityLogReport = React.lazy(() => import('modules/app/reports/activity_log/ActivityLog.js'));
const BillPrintReport = React.lazy(() => import('modules/app/reports/bill_print/BillPrintReport.js'));
const MemberPointsReport = React.lazy(() => import('modules/app/reports/member_points/MemberPointsReport.js'));

const DataDumpReport = React.lazy(() => import('modules/app/reports/data_dump/DataDumpReport.js'));

const MessageReport = React.lazy(() => import('modules/app/reports/message_report/MessageReport.js'));
const ComplaintReport = React.lazy(() => import('modules/app/reports/complaint_report/ComplaintReport.js'));
const ExchangeRateReport = React.lazy(() => import('modules/app/reports/exchange_rate_report/ExchangeRateReport.js'));
const ModuleAndPageAccessRightReport = React.lazy(() => import('modules/app/reports/module_and_page_access_right_report/ModuleandPageAccessRightReport.js'));
const ShiftCloseReport = React.lazy(() => import('modules/app/reports/shift_close_report/ShiftCloseReport.js'));
const GatePassReport = React.lazy(() => import('modules/app/reports/gate_pass_report/GatePassReport.js'));
const AMCReport = React.lazy(() => import('modules/app/reports/amc_report/AMCReport.js'));
const GatePassReturnReport = React.lazy(() => import('modules/app/reports/gate_pass_return_report/GatePassReturnReport.js'));






//  FRONT OFFICE SETUP
const FODefault = React.lazy(() => import('modules/fo/setup/default_setup/DefaultSetup.js'));
const FOSettings = React.lazy(() => import('modules/fo/setup/fo_settings/FOSettings.js'));
const FOUserRight = React.lazy(() => import('modules/fo/setup/user_right/Page.js'));
const GuestStatusSetup= React.lazy(() => import('modules/fo/setup/guest_status/Page.js'));
const HousekeeperSetup = React.lazy(() => import('modules/fo/setup/housekeeper/Page.js'));
const PackageRate = React.lazy(() => import('modules/fo/setup/package_rate/Page.js'));
const RackRate = React.lazy(() => import('modules/fo/setup/rack_rate/Page.js'));
const ReportTemplate = React.lazy(() => import('modules/fo/setup/report_template/Page.js'));
const ReservationModeSetup = React.lazy(() => import('modules/fo/setup/reservation_mode/Page.js'));
const RevenueCode = React.lazy(() => import('modules/fo/setup/revenue_code/Page.js'));
const RevenueType = React.lazy(() => import('modules/fo/setup/revenue_type/Page.js'));
const RoomCategorySetup = React.lazy(() => import('modules/fo/setup/room_category/Page.js'));
const RoomFeatureSetup = React.lazy(() => import('modules/fo/setup/room_feature/Page.js'));
const RoomSetup = React.lazy(() => import('modules/fo/setup/room_setup/Page.js'));
const ChannelManageRate = React.lazy(() => import('modules/fo/setup/channel_manager_rate/Page.js'));
const AirlineSetup = React.lazy(() => import('modules/fo/setup/airlines_setup/Page.js'));
const FlightSetup = React.lazy(() => import('modules/fo/setup/flight_setup/Page.js'));
const ChannelManager = React.lazy(() => import('modules/fo/setup/channel_manager/ChannelManager.js'));
const GuestProfile = React.lazy(() => import('modules/fo/setup/guest_profile/Page.js'));
const TemplateDesign = React.lazy(() => import('modules/fo/setup/template_design/Page.js'));
// const RoomAmenities = React.lazy(() => import('modules/fo/setup/room_amenities/RoomAmenities.js'));
const ReportKeyword = React.lazy(() => import('modules/fo/setup/report_keyword/Page.js'));
const BlockInventory = React.lazy(() => import('modules/fo/setup/block_inventory/BlockInventory.js'));
const MandatoryField = React.lazy(() => import('modules/fo/setup/mandatory_field/MandatoryField.js'));
// const BookingEngine = React.lazy(() => import('modules/fo/setup/booking_engine/BookingEngine.js'));
// const Amenities = React.lazy(() => import('modules/fo/setup/amenities/Amenities.js'));
const FeerAccountSetup = React.lazy(() => import('modules/fo/setup/feer_account_setup/FeerAccountSetup.js'));
const FeerExchangeRateSetup = React.lazy(() => import('modules/fo/setup/feer_exchange_rate_setup/FeerExchangeRateSetup.js'));
const RevenueBudgetV2 = React.lazy(() => import('modules/fo/setup/report_template_v2/Page.js'));
const BookingChannel = React.lazy(() => import('modules/fo/setup/booking_channel/BookingChannel.js'));
const GreetingAppSetting = React.lazy(() => import('modules/fo/setup/greeting_app_setting/GreetingSetting.js'));
const ReportConfiguration = React.lazy(() => import('modules/fo/setup/report_configuration/Page.js'));
const FeerCurrencySetup = React.lazy(() => import('modules/fo/setup/feer_currency_setup/FeerCurrencySetup.js'));
const RoomMapping = React.lazy(() => import('modules/fo/setup/room_mapping/RoomMapping.js'));
const BookingChannelMerchantSetup = React.lazy(() => import('modules/fo/setup/booking_channel_merchant_setup/BookingChannelSetup.js'));



//  FRONT OFFICE TRANSACTION
const AmendStay = React.lazy(() => import('modules/fo/transaction/amend_stay/Page.js'));
const CancelCheckIn = React.lazy(() => import('modules/fo/transaction/cancel_checkin/Page.js'));
const ChangeRate = React.lazy(() => import('modules/fo/transaction/change_rate/Page.js'));
const Checkout = React.lazy(() => import('modules/fo/transaction/checkout/Checkout.js'));
const Discount = React.lazy(() => import('modules/fo/transaction/discount/Page.js'));
const FOBillRePrint = React.lazy(() => import('modules/fo/transaction/fo_billing/FOBillRePrint.js'));
const FOBillSettlement = React.lazy(() => import('modules/fo/transaction/bill_settlement/FOBillSettlement.js'));
const FOBilling = React.lazy(() => import('modules/fo/transaction/fo_billing/FOBilling.js'));
const FOCheckIn = React.lazy(() => import('modules/fo/transaction/reservation_and_checkin/FOCheckIn.js'));
const FOCheckinV2 = React.lazy(() => import('modules/fo/transaction/reservation_checkin_v2/Checkin.js'));
const FOCreditNote = React.lazy(() => import('modules/fo/transaction/fo_credit_note/Page.js'));
const FOCreditNotePrint = React.lazy(() => import('modules/fo/transaction/fo_credit_note_print/Page.js'));
const FODashboard = React.lazy(() => import('modules/fo/transaction/dashboard/Manage.js'));
const FOReservation = React.lazy(() => import('modules/fo/transaction/reservation_and_checkin/FOReservation.js'));
const FOReservationV2 = React.lazy(() => import('modules/fo/transaction/reservation_checkin_v2/Reservation.js'));
const FORevenueBudget = React.lazy(() => import('modules/fo/transaction/revenue_budget/FORevenueBudget.js'))
const FORevenueBudgetV2 = React.lazy(() => import('modules/fo/transaction/revenue_budget_v2/FORevenueBudget.js'))
const FoAdditionalRate = React.lazy(() => import('modules/fo/transaction/additional_rate/AdditionalRate.js'));
const FoLinkDeLinkRooms = React.lazy(() => import('modules/fo/transaction/link_delink_rooms/Page.js'));
const FoPayment = React.lazy(() => import('modules/fo/transaction/payment_room/PaymentRoom.js'));
const FoReceipt = React.lazy(() => import('modules/fo/transaction/receipt_room/ReceiptRoom.js'));
const FoRoomStatusUpdate = React.lazy(() => import('modules/fo/transaction/room_status_update/Page.js'));
const GuestUpdate = React.lazy(() => import('modules/fo/transaction/guest_update/Page.js'));
const RoomRatePosting = React.lazy(() => import('modules/fo/transaction/room_rate_posting/RoomRatePosting.js'));
const RoomRatePostingV2 = React.lazy(() => import('modules/fo/transaction/room_rate_posting_v2/RoomRatePostingV2.js'));
const StopReleasePosting = React.lazy(() => import('modules/fo/transaction/stop_release_posting/StopReleasePosting.js'));
const TransferRooms = React.lazy(() => import('modules/fo/transaction/room_transfer_exchange_room_pax_transfer/Page.js'));
const MessageAndComplain = React.lazy(() => import('modules/fo/transaction/message_and_complaints/MessageAndComplaints.js'));
const FOForeignExchangeEncashment = React.lazy(() => import('modules/fo/transaction/foreign_exchange_encashment/Page.js'));
//const FOBillUpdate = React.lazy(() => import('modules/fo/transaction/bill_update/Page.js'));
const GuestInfoSearch = React.lazy(() => import('modules/fo/transaction/guest_information_search/Page.js'));
const CashBankEntry = React.lazy(() => import('modules/fo/transaction/cash_bank_entry/Page.js'));
const HotelPositionV2 = React.lazy(() => import('modules/fo/transaction/hotel_position_v2/HotelPositionV2.js'));
const FeerCardTransaction = React.lazy(() => import('modules/fo/transaction/feer_card_entry/Page.js'));
const WakeUpEvent = React.lazy(() => import('modules/fo/transaction/wake_up_event/WakeUpEvent.js'));
const GuestHistoryImport = React.lazy(() => import('modules/fo/transaction/guest_history_import/GuestHistoryImport.js'));

//  FRONT OFFICE REPORT
const ArrivalReport = React.lazy(() => import('modules/fo/report/arrival/ArrivalReport.js'));
const ChangeRateReport = React.lazy(() => import('modules/fo/report/change_rate_report/ChangeRateReport.js'));
const CheckinReport = React.lazy(() => import('modules/fo/report/checkin_report/CheckinReport.js'));
const DepartureReport = React.lazy(() => import('modules/fo/report/departure/DepartureReport.js'));
const FOCashierReport = React.lazy(() => import('modules/fo/report/fo_cashier_report/FOCashierReport.js'));
const FODiscountReport = React.lazy(() => import('modules/fo/report/discount_report/DiscountReport.js'));
const FOHighBalanceReport = React.lazy(() => import('modules/fo/report/high_balance_report/HighBalanceReport'));
const FOManagersReport = React.lazy(() => import('modules/fo/report/managers_report/ManagersReport.js'));
const FOMaterializedViewReport = React.lazy(() => import('modules/fo/report/materialized_view/MaterializedView'));
const FOOccupancyReport = React.lazy(() => import('modules/fo/report/occupancy_report/OccupancyReport.js'));
const FORateVarianceReport = React.lazy(() => import('modules/fo/report/rate_variance_report/RateVarianceReport.js'));
const FORevenueBudgetReport = React.lazy(() => import('modules/fo/report/revenue_budget_report/RevenueBudgetReport.js'));
const FORevenueBudgetReportV2 = React.lazy(() => import('modules/fo/report/revenue_budget_report_v2/RevenueBudgetReport.js'));
const FORevenueMateralizedReport = React.lazy(() => import('modules/fo/report/revenue_materalized_report/RevenueMateralizedReport.js'));
const FOSalesTaxRegisterReport = React.lazy(() => import('modules/fo/report/sales_tax_register/SalesTaxRegister'));
const FOVoucherPrint = React.lazy(() => import('modules/fo/report/voucher_print/Page.js'));
const ForecastReport = React.lazy(() => import('modules/fo/report/forecast/ForecastReport.js'));
const GLReport = React.lazy(() => import('modules/fo/report/guest_ledger_report/GuestLedgerReport.js'));
const NightAuditReport = React.lazy(() => import('modules/fo/report/night_audit_report/NightAuditReport.js'));
const RoomFlashReport = React.lazy(() => import('modules/fo/report/room_flash/RoomFlashReport.js'));
const RoomRateOfTheDay = React.lazy(() => import('modules/fo/report/room_rate_of_the_day/RoomRateOfTheDay.js'));
const SalesContributionReport =React.lazy(() => import('modules/fo/report/sales_contribution_report/SalesContributionReport.js'));
const MaterializedAndForecastReport =React.lazy(() => import('modules/fo/report/materialized_and_forecast/MaterializedAndForecastReport.js'));
const TourismReport = React.lazy(()=>import('modules/fo/report/tourism_report/TourismReport.js'));
const GuestMovementReport = React.lazy(()=> import('modules/fo/report/guest_movement_report/GuestMovementReport'));
const ReservationReport =React.lazy(() => import('modules/fo/report/reservation_report/ReservationReport.js'));
const PickUpDropOffReport =React.lazy(() => import('modules/fo/report/pick_up_drop_off_report/PickUpDropOffReport'));
const AmendStayReport = React.lazy(() => import('modules/fo/report/amend_stay/AmendStayReport.js'));
const RoomAllocationReport = React.lazy(() => import('modules/fo/report/room_allocation/RoomAllocationReport.js'));
const RoomStatusLogReport = React.lazy(() => import('modules/fo/report/room_status_log_report/RoomStatusLogReport.js'));
const ComplementaryReport = React.lazy(() => import('modules/fo/report/complementary_report/ComplementaryReport.js'));
const TaxCodeSummaryReport = React.lazy(()=> import('modules/fo/report/tax_code_summary_report/TaxCodeSummaryReport.js'))
const MealPlanSummaryReport = React.lazy(() => import('modules/fo/report/meal_plan_summary_report/MealPlanSummaryReport.js'));
const DailyTransactionEntryReport = React.lazy(() => import('modules/fo/report/daily_transaction_entry_report/DailyTransactionEntryReport.js'));
const ReceiptDetailReport = React.lazy(() => import('modules/fo/report/receipt_detail_report/ReceiptDetailReport.js'));
const ForeignExchangeBalanceReport = React.lazy(() => import('modules/fo/report/foreign_exchange_balance_report/ForeignExchangeBalanceReport.js'));
const RevenueAnalysisReport = React.lazy(() => import('modules/fo/report/revenue_analysis_report/RevenueAnalysisReport.js'));
const FEERCardTransactionReport = React.lazy(() => import('modules/fo/report/feer_card_transaction_report/FEERCardReport.js'));
const FEERCashHandoverReport = React.lazy(() => import('modules/fo/report/feer_cash_handover_report/FEERCashReport.js'));
const StatementOfForeignCurrency = React.lazy(() => import('modules/fo/report/statement_of_foreign_currency/StatementOfForeignCurrency.js'));
const WakeUpEventReport = React.lazy(() => import('modules/fo/report/wake_up_event_report/WakeUpEventReport.js'));
const FEERBuySellReport = React.lazy(() => import('modules/fo/report/buy_sell_report/BuySellReport.js'));
const MonthlyFeerTransactionReport =  React.lazy(() => import('modules/fo/report/monthly_feer_transaction_report/MonthlyFeerReport.js'));
const FeerPLReport =  React.lazy(() => import('modules/fo/report/feer_profit_loss_report/FeerProfitLossReport.js'));
const ReservationAdvanceReport = React.lazy(() => import('modules/fo/report/reservation_advance_report/ReservationAdvanceReport.js'));
const DailySalesReport = React.lazy(() => import('modules/fo/report/daily_sales_report/DailySalesReport.js'));
const FolioReport =  React.lazy(() => import('modules/fo/report/folio_report/FolioReport.js'));
const EarlyLateArrivalDepartureReport =  React.lazy(() => import('modules/fo/report/early_late_arrival_departure_report/EarlyLateArrivalDepartureReport.js'));
const VIPGuestReport =  React.lazy(() => import('modules/fo/report/vip_guest_report/VipGuestReport.js'));
const FolioTransferReport = React.lazy(() => import('modules/fo/report/folio_transfer_report/FolioTransferReport.js'));
// global log report
const LogReport = React.lazy(() => import('modules/fo/report/fo_log_report/FOLogReport.js'));

const AnalysisReport = React.lazy(() => import('modules/fo/report/analysis_report/AnalysisReport.js'));
const Gstr1Report = React.lazy(() => import('modules/fo/report/gstr_1/Gstr1.js'));
// const LogReport = React.lazy(() => import('modules/fo/report/fo_log_report/FOLogReport.js'));
const OutputRegisterReport = React.lazy(() => import('modules/fo/report/output_register_gst/OutputRegister.js'));

//  POINT OF SALE SETUP
const KitchenSetup = React.lazy(() => import('modules/pos/setup/kitchen_setup/Page.js'));
const KotGroup = React.lazy(() => import('modules/pos/setup/kot_group_setup/Page.js'));
const KotGroupAndMenuLink = React.lazy(() => import('modules/pos/setup/kot_group_and_menu_link/Page.js'));
const MenuGroup = React.lazy(() => import('modules/pos/setup/menu_group/Page.js'));
const NCAuthorityOutletLink = React.lazy(() => import('modules/pos/setup/outlet_nc_authority_link/Page.js'));
const NCAuthoritySetup = React.lazy(() => import('modules/pos/setup/nc_authority_setup/Page.js'));
const OpenItemSetup = React.lazy(() => import('modules/pos/setup/outlet_open_item/Page.js'));
const OutletMenuGroup = React.lazy(() => import('modules/pos/setup/outlet_menu_group/Page.js'));
const POSContractRateSetup = React.lazy(() => import('modules/pos/setup/contract_rate/Page.js'));
const POSFunctionType = React.lazy(() => import('modules/pos/setup/function_type_setup/Page.js'));
const POSHappyHourSetup = React.lazy(() => import('modules/pos/setup/happy_hour/Page.js'));
const POSMemberGroup = React.lazy(() => import('modules/pos/setup/membership_group_setup/Page.js'));
const POSMembership = React.lazy(() => import('modules/pos/setup/membership_setup/Page.js'));
const POSMenuCagetory = React.lazy(() => import('modules/pos/setup/menu_category/Page.js'));
const POSMenuSetup = React.lazy(() => import('modules/pos/setup/menu_setup/Page.js'));
const POSOrderType = React.lazy(() => import('modules/pos/setup/order_type/Page.js'));
const POSOutletAttendant = React.lazy(() => import('modules/pos/setup/outlet_attendant/Page.js'));
const POSOutletAttendantLink = React.lazy(() => import('modules/pos/setup/outlet_attendant_link/Page.js'));
const POSOutletOrderType = React.lazy(() => import('modules/pos/setup/outlet_order_type/Page.js'));
const POSOutletSession = React.lazy(() => import('modules/pos/setup/outlet_session/Page.js'));
const POSOutletSetup = React.lazy(() => import('modules/pos/setup/outlet_setup/Page.js'));
const POSOutletTable = React.lazy(() => import('modules/pos/setup/outlet_table/Page.js'));
const POSSession = React.lazy(() => import('modules/pos/setup/session/Page.js'));
const POSUserRights = React.lazy(() => import('modules/pos/setup/user_rights/Page.js'));
// const POSVoucherCodeSetup = React.lazy(() => import('modules/pos/setup/voucher_code/Page.js'));
const POSOutletCounterSetup = React.lazy(() => import('modules/pos/setup/outlet_counter_setup/Page.js'));
const CounterGroup = React.lazy(() => import('modules/pos/setup/counter_group/Page.js'));
const CounterKitchenMapping = React.lazy(() => import('modules/pos/setup/counter_kitchen_mapping/Page.js'));
const SeatingArrangement = React.lazy(() => import('modules/pos/setup/seating_arrangement/SeatingArrangement.js'));
const POSTemplateDesign = React.lazy(() => import('modules/pos/setup/template_design/Page.js'));
const NCUserSetup = React.lazy(() => import('modules/pos/setup/nc_user/Page.js'));
const TicketMapping = React.lazy(() => import('modules/pos/setup/ticket_mapping/TicketMapping.js'));
const VariantType = React.lazy(() => import('modules/pos/setup/variant_type_setup/Page.js'));
const Variant = React.lazy(() => import('modules/pos/setup/variant_setup/Page.js'));
const PreOrderFor = React.lazy(() => import('modules/pos/setup/pre_order_for/Page.js'));
const PreOrderSettings = React.lazy(() => import('modules/pos/setup/pre_order_settings/Page.js'));

//  POINT OF SALE TRANSACTION
const CoverUpdate = React.lazy(() => import('modules/pos/transaction/cover_update/CoverUpdate.js'));
const POSBillRePrint = React.lazy(() => import('modules/pos/transaction/POSBillRePrint.js'));
const POSBillSettlement = React.lazy(() => import('modules/pos/transaction/POSBillSettlement.js'));
const POSCityLedger = React.lazy(() => import('modules/pos/transaction/city_ledger/CityLedger.js'));
const POSCreditNote = React.lazy(() => import('modules/pos/transaction/POSCreditNote.js'));
const POSCreditNotePrint = React.lazy(() => import('modules/pos/transaction/POSCreditNotePrint.js'));
const POSDashboard = React.lazy(() => import('modules/pos/transaction/POSDashboard.js'));
const POSOrder = React.lazy(() => import('modules/pos/transaction/order/POSOrder.js'));
const POSTouchOrder = React.lazy(() => import('modules/pos/transaction/order/POSOrder.js'));
const PosBilling = React.lazy(() => import('modules/pos/transaction/POSBilling.js'));
const Production = React.lazy(() => import('modules/pos/transaction/production/Production.js'));
const ProductionV2 = React.lazy(() => import('modules/pos/transaction/productionV2/ProductionV2.js'));
const EventBooking = React.lazy(() => import('modules/pos/transaction/event_booking/Page.js'));
// const TicketMapping = React.lazy(() => import('modules/pos/transaction/ticket_mapping/TicketMapping.js'));
const EventBillTagging = React.lazy(() => import('modules/pos/transaction/event_bill_tagging/EventBillTagging.js'));
const TicketVerification = React.lazy(() => import('modules/pos/transaction/ticket_verification/Page.js'));
const EventDashboard = React.lazy(() => import('modules/pos/transaction/event_dashboard/EventDashboard.js'));
const PreOrder = React.lazy(() => import('modules/pos/transaction/pre_order/PreOrder.js'));


//  POINT OF SALE REPORT
const POSCancellationReport = React.lazy(() => import('modules/pos/report/cancellation_report/CancellationReport'));
const POSCashierReport = React.lazy(() => import('modules/pos/report/cashier_report/CashierReport'));
const POSDiscountReport = React.lazy(() => import('modules/pos/report/discount_report/DiscountReport'));
const POSMaterializedViewReport = React.lazy(() => import('modules/pos/report/materialized_view/MaterializedView'));
const POSNCReport = React.lazy(() => import('modules/pos/report/nc_report/NCReport'));
const POSSalesByItemReport = React.lazy(() => import('modules/pos/report/sales_by_item_report/SalesByItemReport'));
const POSSalesSummaryReport = React.lazy(() => import('modules/pos/report/sales_summary_report/SalesSummaryReport'));
const POSTicketAnalysisReport = React.lazy(() => import('modules/pos/report/ticket_analysis_report/TicketAnalysisReport'));
const POSEventReservationReport = React.lazy(() => import('modules/pos/report/event_reservation_report/EventReservationReport'));
const POSTransactionSummary = React.lazy(() => import('modules/pos/report/transaction_summary_report/TransactionSummaryReport'));
const PosCoverAnalysisReport = React.lazy(() => import('modules/pos/report/cover_analysis/CoverAnalysisReport'));
const POSDeliveryReport = React.lazy(() => import('modules/pos/report/delivery_report/DeliveryReport'));
const ReportPrint = React.lazy(() => import('modules/pos/report/report_print/Page'));
const POSTransferItemReport = React.lazy(() => import('modules/pos/report/item_transfer/TransferItemReport'));
const EventMaterializedAndForecast = React.lazy(() => import('modules/pos/report/event_materialized_and_forecast/EventMaterializedAndForecast'));
const POSProductionReport = React.lazy(() => import('modules/pos/report/production_report/ProductionReport'));

//  INVENTORY SETUP
const INVSettings = React.lazy(() => import('modules/inv/setup/inv_setting/INVSettings.js'));
const InvUserRights = React.lazy(() => import('modules/inv/setup/user_rights/Page.js'));
const OpeningStock = React.lazy(() => import('modules/inv/setup/opening_stock/OpeningStockSetup.js'));
const StoreGroupAccessRight = React.lazy(() => import('modules/inv/setup/store_group_access_right/StoreGroupAccessRight.js'));
const StoreItemGroupSetup = React.lazy(() => import('modules/inv/setup/store_item_group_setup/StoreItemGroupSetup.js'));
const StoreItemSetup = React.lazy(() => import('modules/inv/setup/store_item_setup/StoreItemSetup.js'));
const StoreSetup = React.lazy(() => import('modules/inv/setup/store_setup/StoreSetup.js'));
const StoreStartPeriod = React.lazy(() => import('modules/inv/setup/store_start_date_setup/StoreStartDateSetup.js'));
const StoreLockPeriod = React.lazy(() => import('modules/inv/setup/store_lock_period/StoreLockPeriodSetup'));
const VendorContractSetup = React.lazy(() => import('modules/inv/setup/vendor_contract_setup/VendorContractSetup.js'));
const INVAgeingDefination = React.lazy(() => import('modules/inv/setup/inv_ageing_defination/Page.js'));
const PhysicalStockEntry = React.lazy(() => import('modules/inv/setup/physical_stock_entry/Page.js'));
const LocationSetup = React.lazy(() => import('modules/inv/setup/location_setup/Location.js'));


//  INVENTORY TRANSACTION
const Adjustment = React.lazy(() => import('modules/inv/transaction/adjustment/Adjustment.js'));
const InvDashboard = React.lazy(() => import('modules/inv/transaction/dashboard/Page.js'));
const IssueDirect = React.lazy(() => import('modules/inv/transaction/issue_direct/Page.js'));
const IssueIndent = React.lazy(() => import('modules/inv/transaction/issue_indent/Page.js'));
const IssueReturn = React.lazy(() => import('modules/inv/transaction/issue_return/Page.js'));
const POSecurityCheck = React.lazy(() => import('modules/inv/transaction/po_security_check/Page.js'));
const PurchaseOrder = React.lazy(() => import('modules/inv/transaction/purchase_order/Page.js'));
const PurchaseRequest = React.lazy(() => import('modules/inv/transaction/purchase_request/PurchaseRequest.js'));
const Receipt = React.lazy(() => import('modules/inv/transaction/receipt/Receipt.js'));
const ReceiptReturn = React.lazy(() => import('modules/inv/transaction/receipt_return/ReceiptReturn.js'));
const StoreRequest = React.lazy(() => import('modules/inv/transaction/store_request/Page.js'));
const TransferItem = React.lazy(() => import('modules/inv/transaction/transfer_item/Page.js'));
const TransferCostCenter = React.lazy(() => import('modules/inv/transaction/modify_cost_center/Page.js'));
const Purge = React.lazy(() => import('modules/inv/transaction/purge/Page.js'));

//  INVENTORY REPORT
const AdjustmentReport = React.lazy(() => import('modules/inv/report/adjustment/AdjustmentReport.js'));
const INVVoucherPrint = React.lazy(() => import('modules/inv/report/voucher_print_v2/Page.js'));
const IssueReport = React.lazy(() => import('modules/inv/report/issue/IssueReport.js'));
const IssueReturnReport = React.lazy(() => import('modules/inv/report/issue_return/IssueReturnReport.js'));
const PurchaseOrderReport = React.lazy(() => import('modules/inv/report/purchase_order/PurchaseOrderReport.js'));
const PurchaseRegister = React.lazy(() => import('modules/inv/report/purchase_register/PurchaseRegisterReport.js'));
const PurchaseRequestReport = React.lazy(() => import('modules/inv/report/purchase_request/PurchaseRequestReport.js'));
const ReceiptReport = React.lazy(() => import('modules/inv/report/receipt/ReceiptReport.js'));
const ReceiptReturnReport = React.lazy(() => import('modules/inv/report/receipt_return/ReceiptReturnReport.js'));
const StockLedgerReport = React.lazy(()=> import('modules/inv/report/stock_ledger/StockLedgerReport.js'));
const StockReport = React.lazy(() => import('modules/inv/report/stock_report/StockReport.js'));
const StoreRequestReport = React.lazy(() => import('modules/inv/report/store_request/StoreRequestReport.js'));
const StoreSummaryReport = React.lazy(() => import('modules/inv/report/store_summary/StoreSummaryReport.js'));
const AgeingDefinationReport = React.lazy(() => import('modules/inv/report/inv_ageing_report/INVAgeingReport.js'));
const ExpiryItemListReport = React.lazy(() => import('modules/inv/report/expiry_item_list/ExpiryItemList.js'));
const PhysicalStockReport = React.lazy(() => import('modules/inv/report/physical_stock_report/PhysicalStockReport.js'));
const TransferItemReport = React.lazy(() => import('modules/inv/report/transfer_item_report/TransferItemReport.js'));
const VendorWiseItemAnalysisReport = React.lazy(() => import('modules/inv/report/vendor_wise_item_analysis_report/VendorWiseItemAnalysisReport.js'));
// const ReceiptReportv2 = React.lazy(() => import('modules/inv/report/receipt_report_v2/ReceiptReportv2.js'));receipt report v2 not released for now *
const InputRegisterReport = React.lazy(() => import('modules/inv/report/input_register_gst/InputRegister.js'));


//  ACCOUNT SETUP
const ACSettings = React.lazy(() => import('modules/ac/setup/ac_settings/ACSettings.js'));
const ACTemplateDesign = React.lazy(() => import('modules/ac/setup/template_design/Page.js'));
const AgeingDefination = React.lazy(() => import('modules/ac/setup/ageing_defination/Page.js'));
const ChartOfAccountSetup = React.lazy(() => import('modules/ac/setup/chart_of_account/ChartOfAccountSetup.js'));
const LinkAR = React.lazy(() => import('modules/ac/setup/link_ar/LinkAR.js'));
const LinkExmptTax = React.lazy(() => import('modules/ac/setup/link_exmpt_tax/LinkExmptTax.js'));
const LinkInventory = React.lazy(() => import('modules/ac/setup/link_inventory/LinkInventory.js'));
const LinkSales = React.lazy(() => import('modules/ac/setup/link_sales/Page.js'));
const MainHeadSetup = React.lazy(() => import('modules/ac/setup/main_head/MainHeadSetup.js'));
const RetainedEarning = React.lazy(() => import('modules/ac/setup/retained_earning/RetainedEarningSetup.js'));
const StartDateSetup = React.lazy(() => import('modules/ac/setup/start_date/StartDateSetup.js'));
const DateLockSetup = React.lazy(() => import('modules/ac/setup/date_lock/DateLockSetup'));
const SubHeadSetup = React.lazy(() => import('modules/ac/setup/sub_head/SubHeadSetup.js'));
const SubLedgerSetup = React.lazy(() => import('modules/ac/setup/sub_ledger/SubLedgerSetup.js'));
const VoucherTypeRight = React.lazy(() => import('modules/ac/setup/voucher_type_right/VoucherTypeRight.js'));
const VoucherTypeSetup = React.lazy(() => import('modules/ac/setup/voucher_type/VoucherTypeSetup.js'));
const openingBalanceEntry = React.lazy(() => import('modules/ac/setup/opening_balance_entry_v2/OpeningBalanceEntry.js'));
const ACReportTemplate = React.lazy(() => import('modules/ac/setup/ac_report_template/ACReportTemplate.js'));
const LinkPayroll = React.lazy(() => import('modules/ac/setup/link_payroll/page.js'));
const PostingLinkSetting = React.lazy(() => import('modules/ac/setup/posting_link_setting/page.js'));
const LinkSalesCode = React.lazy(() => import('modules/ac/setup/link_sales_code/Page.js'));
//  ACCOUNT TRANSACTION
//const ACCDashboard = React.lazy(() => import('modules/ac/transaction/dashboard/Page.js'));
const AcBillMatching = React.lazy(() => import('modules/ac/transaction/bill_matching/Page.js'));
const AcCreditNote = React.lazy(() => import('modules/ac/transaction/credit_note/Page.js'));
const AcDebitNote = React.lazy(() => import('modules/ac/transaction/debit_note/Page.js'));
const BankReconciliation = React.lazy(() => import('modules/ac/transaction/bank_reconciliation/Page.js'));
const ConsumptionPosting = React.lazy(() => import('modules/ac/transaction/consumption_posting/Page.js'));
const StatementInvoice = React.lazy(()=> import('modules/ac/transaction/statement_invoice/StatementInvoiceSetup.js'));
const periodicalExpenses = React.lazy(() => import('modules/ac/transaction/periodical_expenses/PeriodicalExpenses.js'));
const purchasePosting = React.lazy(() => import('modules/ac/transaction/purchase_posting/PurchasePosting.js'));
const salesPosting = React.lazy(() => import('modules/ac/transaction/sales_posting/SalesPosting.js'));
const transactionEntry = React.lazy(() => import('modules/ac/transaction/transaction_entry/TransactionEntry.js'));
const BillTransfer = React.lazy(() => import('modules/ac/transaction/bill_transfer/BillTransfer.js'));
const billEntry = React.lazy(() => import('modules/ac/transaction/bill_entry/Page.js'));
const ACPLBudget = React.lazy(() => import('modules/ac/transaction/ac_pl_budget/ACPLBudget.js'));
const ChequePrinting = React.lazy(() => import('modules/ac/transaction/cheque_printing/Page.js'));
const ACPayrollPosting = React.lazy(() => import('modules/ac/transaction/payroll_posting/PayrollPosting.js'));
const InvoiceIntegration = React.lazy(() => import('modules/ac/transaction/invoice_integration/Page.js'));
const TransactionEntryImport = React.lazy(() => import('modules/ac/transaction/transaction_entry_import/TransactionEntryImport.js'));
const SalesImport = React.lazy(() => import('modules/ac/transaction/sales_import/SalesImport.js'));
const ProductionPosting = React.lazy(() => import('modules/ac/transaction/production_posting/ProductionPosting.js'));
const DebtorsFollowUp = React.lazy(() => import('modules/ac/transaction/debtors_followup/DebtorsFollowUp.js'));
const FollowUpReminder = React.lazy(() => import('modules/ac/transaction/follow_up_reminder/Page.js'));

//  ACCOUNT REPORT
const ACVoucherPrint = React.lazy(() => import('modules/ac/report/voucher_print_v2/Page.js'));
const AccountLinkReport = React.lazy(() => import('modules/ac/report/link_report/LinkReport.js'));
const AgeingReport = React.lazy(() => import('modules/ac/report/ageing_report/AgeingReport.js'));
const BalanceSheetReport = React.lazy(() => import('modules/ac/report/balance_sheet/BalanceSheetReport.js'));
const BankReconciliationReport = React.lazy(() => import('modules/ac/report/bank_reconciliation/BankReconciliationReport.js'));
const CashFlowStatement = React.lazy(() => import('modules/ac/report/bank_and_cash_flow_statement/BankAndCashFlowStatement.js'));
const LedgerReport = React.lazy(() => import('modules/ac/report/ledger_report/LedgerReport.js'));
const ProfitLossReport = React.lazy(() => import('modules/ac/report/pl_report/ProfitLossReport.js'));
const RemainingPostingReport = React.lazy(() => import('modules/ac/report/remaining_posting/RemainingPostingReport.js'));
const TrialBalanceReport = React.lazy(() => import('modules/ac/report/trial_balance_v2/TrialBalanceReport.js'));
const TransactionCheckList = React.lazy(() => import('modules/ac/report/transaction_checklist/TransactionCheckList.js'));
const ACSettlementReport =  React.lazy(() => import('modules/ac/report/settlement_report/SettlementReport'));
const TransactionLogReport = React.lazy(() => import('modules/ac/report/transaction_log_report/TransactionLogReport.js'));
const TaxReport = React.lazy(()=> import('modules/ac/report/tax_report/TaxReport.js'))
const TDSReport = React.lazy(()=> import('modules/ac/report/tds_report/TdsReport.js'))
const StockReconcileReport = React.lazy(() => import('modules/ac/report/stock_reconcile_report/StockReconcileReport.js'));
//const CostReconciliationReport = React.lazy(() => import('modules/ac/report/cost_reconciliation_report/CostReconciliationReport.js'));
const BillWiseRevenueReport = React.lazy(() => import('modules/ac/report/bill_wise_revenue_report/BillWiseRevenueReport.js'));
//const CollectionReport = React.lazy(() => import('modules/ac/report/collection_report/CollectionReport'));
const LedgerReconcileReport = React.lazy(() => import('modules/ac/report/ledger_reconcile_report/LedgerReconcileReport'));
const ReconcileReport = React.lazy(() => import('modules/ac/report/reconcile_report/ReconcileReport.js'));
const ACMaterializedViewReport = React.lazy(() => import('modules/ac/report/materialized_view/MaterializedView'));
const DebtorsBalanceReport = React.lazy(() => import('modules/ac/report/debtors_balance_report/DebtorsBalanceReports.js'));
const MonthlyVATSubmissionReport = React.lazy(() => import('modules/ac/report/monthly_vat_submission_report/MonthlyVATSubmissionReport.js'));
const ACLogReport = React.lazy(() => import('modules/ac/report/log_report/ACLogReport.js'));
const DebtorsFollowUpReport = React.lazy(() => import('modules/ac/report/debtors_follow_up_report/DebtorsFollowUpReport.js'));
const DebtorsFollowUpCallLogReport = React.lazy(() => import('modules/ac/report/debtors_follow_up_call_log_report/DebtorsFollowUpCallLogReport.js'))


//  COSTING SETUP
const CostExtractionLinkingPart = React.lazy(() => import('modules/cst/setup/cost_extraction_linking/Page.js'));
const CSTStartDate = React.lazy(()=>import('modules/cst/setup/cst_start_date/CSTStartDateSetup.js'));
const ReceipePart = React.lazy(() => import('modules/cst/setup/receipe/Page.js'));
const CostReconcileReportSetup = React.lazy(() => import('modules/cst/setup/cost_reconcile_report_setup/Page.js'));

//  COSTING TRANSACTION
const CostExtraction = React.lazy(() => import('modules/cst/transaction/cost_extraction/Page.js'));
const EventMenuCosting = React.lazy(() => import('modules/cst/transaction/event_menu_costing/EventMenuCosting.js'));
const MenuMix = React.lazy(() => import('modules/cst/transaction/menu_mix/MenuMix.js'));
const CostExtractionV2 = React.lazy(() => import('modules/cst/transaction/cost_extraction_v2/Page.js'));

//  CST REPORT
const CostRecipeReport = React.lazy(() => import('modules/cst/report/recipe_report/ReceipeReport.js'));
const CostReport = React.lazy(() => import('modules/cst/report/cost_report/CostReport.js'));
const EventMenuCostingReport = React.lazy(() => import('modules/cst/report/event_menu_costing_report/EventMenuCostingReport.js'));
const CostReconciliationReport = React.lazy(() => import('modules/cst/report/cost_reconciliation_report/CostReconciliationReport.js'));
const MenuMixReport = React.lazy(() => import('modules/cst/report/menu_mix_report/MenuMixReport.js'));



//  HOUSE KEEPING SETUP
const RoomStatusSetup = React.lazy(() => import('modules/hk/setup/room_status_setup/RoomStatusSetup.js'));
const TaskTypeSetup = React.lazy(() => import('modules/hk/setup/task_type_setup/TaskTypeSetup.js'));
const AmenitiesType = React.lazy(()=>import('modules/hk/setup/amenities_type/AmenitiesType.js') )
const RoomAmenities = React.lazy(() => import('modules/hk/setup/room_amenities/RoomAmenities.js'));
const LaundryItem = React.lazy(() => import('modules/hk/setup/laundry_item_setup/LaundryItemSetup.js'));
const HKAreaSetup = React.lazy(() => import('modules/hk/setup/hk_area_setup/HKAreaSetup.js'));

//  HOUSE KEEPING TRANSACTION
const HKDashboard = React.lazy(() => import('modules/hk/transaction/dashboard/Manage.js'));
const HKLostAndFound = React.lazy(() => import('modules/hk/transaction/lost_and_found/LostAndFound.js'));
const HKRoomStatus = React.lazy(() => import('modules/hk/transaction/room_status/RoomStatus.js'));
const LinkAmenities = React.lazy(() => import('modules/hk/transaction/amenities_consumption_v2/Page.js'));
const HousekeepingTask = React.lazy(() => import('modules/hk/transaction/housekeeping_task/HousekeepingTask.js'));

//  HOUSE KEEPING REPORT
const HKDiscrepancyReport = React.lazy(() => import('modules/hk/report/room_status_discrepancy/DiscrepancyReport.js'));
const LostAndFoundReport = React.lazy(() => import('modules/hk/report/lost_and_found_report/LostAndFoundReport.js'));
const AmenitiesConsumptionReport = React.lazy(() => import('modules/hk/report/amenities_consumption_report/AmenitiesConsumptionReport.js'));

//HR Setup
const StaffProfile = React.lazy(() => import('modules/hr/setup/staff_profile_setup/StaffProfileSetup.js'));
const PaySlip = React.lazy(() => import('modules/hr/setup/pay_slip_setup/PaySlipSetup.js'));
const PayrollHead = React.lazy(() => import('modules/hr/setup/payroll_head/PayrollHeadSetup.js'));
const HRTemplateDesign = React.lazy(() => import('modules/hr/setup/template_design/Page.js'));
const StaffClassification = React.lazy(() => import('modules/hr/setup/staff_classification/StaffClassification.js'));
const HRUserRights = React.lazy(() => import('modules/hr/setup/user_right/Page.js'));
const LoanTypeSetup = React.lazy(() => import('modules/hr/setup/loan_type_setup/LoanTypePage.js'));
const LoanPaymentTypeSetup = React.lazy(() => import('modules/hr/setup/loan_payment_type/LoanPaymentType.js'));
const HRSettings = React.lazy(() => import('modules/hr/setup/hr_settings/HRSettings.js'));

//HR Transaction
const PayrollPosting = React.lazy(() => import('modules/hr/transaction/payroll_posting/Page.js'));
const LoanApplyForm = React.lazy(() => import('modules/hr/transaction/loan_apply_form/LoanApplyForm.js'));
const LoanRepayment = React.lazy(() => import('modules/hr/transaction/loan_repayment/LoanRepayment.js'));

//HR Report
const StaffPayrollHistoryReport = React.lazy(() => import('modules/hr/report/staff_payroll_history/StaffPayrollHistory.js'));
const PayslipStructureReport = React.lazy(() => import('modules/hr/report/payslip_structure_report/PayslipStructure.js'));
const BankStatementReport = React.lazy(() => import('modules/hr/report/bank_statement/BankStatement.js'));
const PayrollTransactionReport = React.lazy(() => import('modules/hr/report/earning_deduction_report/EarningDeductionReport.js'));
const HRTaxReport = React.lazy(() => import('modules/hr/report/tax_report/TaxReport.js'));
const StaffProfileDetailReport = React.lazy(() => import('modules/hr/report/staff_profile_detail_report/StaffProfileDetailReport.js'));
const StaffAttendanceReport = React.lazy(() => import('modules/hr/report/staff_attendance_report/StaffAttendanceReport.js'));
const PayslipvsPostedReport = React.lazy(() => import('modules/hr/report/payslip_vs_posted_report/PayslipvsPostedReport.js'));
const LoanReport = React.lazy(() => import('modules/hr/report/loan_report/LoanReport.js'));

// BE Setup
const BEConfiguration = React.lazy(() => import('modules/be/setup/configuration/Page.js'));
const BERoomRateDetails = React.lazy(() => import('modules/be/setup/roomratedetails/Page.js'));
const BEAmenities = React.lazy(() => import('modules/be/setup/amenities/Amenities.js'));
const BERate = React.lazy(() => import('modules/be/setup/rate/Page.js'));

// TEL Setup
const ExtensionMapping = React.lazy(() => import('modules/tel/setup/extension_mapping/ExtensionMapping.js'));
const SlabRateConfiguration = React.lazy(() => import('modules/tel/setup/slab_rate_configuration/SlabRateConfiguration.js'));
const TELCountryGroup = React.lazy(() => import('modules/tel/setup/country_group/CountryGroup.js'));
const TELCallIdentifier = React.lazy(() => import('modules/tel/setup/call_identifier/CallIdentifier.js'));
const TELSettings = React.lazy(() => import('modules/tel/setup/tel_settings/TELSettings.js'));

// TEL Transaction
const ExtensionPermission = React.lazy(() => import('modules/tel/transaction/extension_permission/Page.js'));

// TE Report
const CallLogReport = React.lazy(() => import('modules/tel/report/call_log_report/CallLogReport.js'));



//SNM Transaction   
const Task=React.lazy(() => import('modules/snm/transaction/task/Page.js'));
// SNM Setup
const ContactSetup = React.lazy(() => import('modules/snm/setup/contact/ContactSetup.js'));
const SNMUserRight = React.lazy(() => import('modules/snm/setup/user_rights/Page.js'));
const CallStatusSetup = React.lazy(() => import('modules/snm/setup/call_status/CallStatusSetup.js'));
const PipelineSetup = React.lazy(() => import('modules/snm/setup/pipeline/Pipeline.js'));
const DealForSetup = React.lazy(() => import('modules/snm/setup/deal_for/DealForSetup.js'));

//SNM Transaction
const LeadTransaction = React.lazy(() => import('modules/snm/transaction/lead/LeadTransaction.js'));
const CallLog = React.lazy(() => import('modules/snm/transaction/call_log/CallLog.js'));

//SNM Report
const LeadAndActivityLogReport = React.lazy(() => import('modules/snm/report/lead_and_activity_log_report/LeadAndActivityLogReport.js'));

//Fixed Asset Setup
const AssetsType = React.lazy(() => import('modules/fa/setup/asset_type/AssetType.js'));
const AssetMangement = React.lazy(() => import('modules/fa/setup/asset_management/AssetManagement.js'));

//FIXED ASSET REGISTER
const FixedAssetRegister = React.lazy(() => import('modules/fa/reports/fixed_asset_register/FixedAssetRegister.js'));


//  OTHER
const AuthorizationFailureScreen = React.lazy(() => import('./Unauthorized.js'));
const DesignDashboard = React.lazy(() => import('modules/design/Dashboard.js'));
const PageNotFound = React.lazy(() => import('./PageNotFound.js'));





function AppRoute(props) {

    const { appState, setAppState, isSubWindow } = useContext(AppContext)
    const { changeLanguage } = useTranslation()

    var ac_date = appState?.ac_date;
    var property_name = appState?.property_name;
    var multi_property_code = appState?.multi_property_code;
    var system_files_access_code = appState?.system_files_access_code;
    var force_password_change = appState?.user?.force_password_change;
    var product_name = appState?.product;
    var product_version_name = appState?.product_version;
    var license = appState?.license;
    var onScreenKeyboardVisible = appState?.onScreenKeyboardVisible;

    if (ac_date == null) ac_date = (<span className='as-text-bold'>Loading...</span>);
    else ac_date = ToDateString(ac_date);


    //  Create Refs
    const ref_change_password = useRef();


    //  Create States
    const [reloadMenu, setReloadMenu] = useState(0);
    const [moduleHasDashboard, setModuleHasDashboard] = useState(false)
    const [moduleHasStockReport, setModuleHasStockReport] = useState(false)
    const [isTouchOrderScreenActive, setIsTouchOrderScreenActive] = useState(false)
    const [openSignaturePopup,setOpenSignaturePopup]=useState(false);
    const [setLoaderStatus, loading, isLoading] = useLoader();
    const [SignatureUploadAllowed, setSignatureUploadAllowed] = useState(false);


    //  Modal Stack Hook
    const [modal, push, pop] = useModalStack();


    //  History Hook
    const {location} = useHistory()
    const currentURLLocation = useLocation()


    //  Action on change of force_password_change and location
    useEffect(() => {
        if(force_password_change) OpenPWChangeModal(force_password_change)
    }, [force_password_change, location])

    useEffect(() => {
        Getsignatureupload();
    }, [SignatureUploadAllowed])



    const Getsignatureupload = () => {
        setLoaderStatus("getsignatureupload", true);
        ApiRequest({
          TYPE: "GET",
          URL: "/app/Appcommon/IsSignatureUploadAllowed",
          CALLBACK: function (res) {
            ResponseSnackbar(res);
            if (res.error_code == 0) {
              const data = res.data.signature_upload_data
              setSignatureUploadAllowed(data.allow_signature_upload);
            }
          },
          COMPLETE: function () {
            setLoaderStatus("getsignatureupload", false);
          },
          DATA: {},
        });
      };



    //  Open Password Change Modal
    const OpenPWChangeModal = (is_forced) => {
        push(
            <ModalPopUp
                size = "x-small"
                pop = {is_forced ? () => {} : pop}
                key = {1}
                body = {<ChangePassword pop={is_forced ? () => {} : pop} forcepop={() => pop()} ref={ref_change_password} />}
                header = {is_forced ? "Update Your Password Before Proceeding" : "Change Password"}
                footer = {
                    <button
                        onClick={() => ref_change_password.current.UpdatePassword()}
                        type="button"
                        className="as-default-btn-solid"
                    >
                        Update
                    </button>
                }
            />
        )
    };

    const OpenPropertySwitchModal = () => {
        push(
            <ModalPopUp
                size = "x-small"
                pop = {pop}
                key = {1}
                body = {<PropertySwitch pop={pop}/>}
                header = {"Switch Property"}
                footer = {
                    <></>
                }
            />
        )
    };


    //  Check if url has changed
    useEffect(() => {
        if(currentURLLocation) {
            var url = currentURLLocation.pathname
            if(url.includes('/fo/') || url.includes('/pos/')) setModuleHasDashboard(true)
                else setModuleHasDashboard(false)

            if(url.includes('/inv/')) setModuleHasStockReport(true)
            else setModuleHasStockReport(false)

            if(url.includes('/aegistouchorder')) setIsTouchOrderScreenActive(true)
                else setIsTouchOrderScreenActive(false)
        }
    }, [currentURLLocation])


    //  Go To Dashboard of module that is active now
    const goToModuleDashboard = () => {
        //  This will match anything after the first '/' until we encounter another '/'
        var current_module = window.location.pathname.replace(/^\/([^\/]*).*$/, '$1')
        //  Append dashboard to the active module and redirect to it.
        window.open(`${current_module}/dashboard`, '_self')
    }

    const showQRCode = () => {
        var api_url = (window.location.protocol || 'https:') + '//' + window.location.host + '/api'
        push(
            <ModalPopUp
                size={'x-small'}
                bodyOverflow={'visible'}
                pop = {pop}
                key = {1}
                body = {<div className='row'>
                            <div className='as-form-wrapper'>
                            <div className='col-12 col-m-12 col-s-12 col-x-12 as-text-center'>
                                    <QRCode
                                        style={{ height: '100%', width: '100%' }}
                                        value={api_url}
                                    />
                            </div>
                            </div>

                </div>}
                header={<span className="as-modal-popup-header">USE THIS QR TO ACCESS AEGIS RESTRO</span>}
                footer = {
                    <>
                        <button
                            onClick={() => pop()}
                            type="button"
                            className="as-default-btn-solid"
                        >
                            Close
                        </button>
                    </>
                }
            />
        )
    }

        //on click of the uploa signature button to open attachment state is set to true 
    //ifopenSignaturePopup is true attachement opopup will be called
    const OnSignatureClick = () => {
     setOpenSignaturePopup(true);
    };


    //shortcut keys
    useEffect(() => {
        var r1 = AddEvent_FUNCTION('1', () => {
            if(currentURLLocation && currentURLLocation.pathname.includes('/inv/')){
                NewWindow(`/inv/report/stock?t=1&url=${true}`)
            }
        }) 
        return () => {
            r1(); 
        }
    });
    
    const handleLanguageChange = (lang) => {
        setLoaderStatus('changeLanguage', true, null, 'Translating Language...')
        changeLanguage(lang.short_name)
        localStorage.setItem('language', lang.short_name)
        
        ApiRequest({
            TYPE: 'POST',
            URL: '/Application/setUserLocalizationLanguage',
            DATA: {
                localization_language_id: lang.value
            },
            CALLBACK: function(response) {
                ResponseSnackbar(response);
            },
            COMPLETE: () => {
                setTimeout(() => {
                    setLoaderStatus('changeLanguage', false)
                }, 2500)
            }
        })
    }

    
    const toggleOnScreenKeyboard = () => {
        var prevVisibility = appState.onScreenKeyboardVisible
        setAppState(prevState => ({
            ...prevState,
            onScreenKeyboardVisible: !prevVisibility
        }))
        localStorage.setItem('onScreenKeyboardVisible', !prevVisibility)
    }


    //  MAIN COMPONENT
    return (
        <>
            {openSignaturePopup ? (
                <UploadSignature
                    open={openSignaturePopup}
                    setOpenSignaturePopup={setOpenSignaturePopup}
                />
            ) : <></>}

            

            <div className={`as-body-content${isSubWindow ? ' sub-window' : ''}`} id="bodyContent">
                {isLoading('changeLanguage')
                ? loading(false, true, 100, true)
                : null}
                <div className='as-body-content-top-first-row'>
                    <div className='row'>
                        <div className='col-6 col-m-6 col-s-6 col-x-6 as-body-content-top-block as-no-padding-top'>
                            <FontAwesomeIcon icon={faCalendarDay} />
                            <span className='space-left as-text-bold'>{ac_date}</span>
                        </div>
                        {
                            !isSubWindow
                            &&
                        
                            <div className='col-6 col-m-6 col-s-6 col-x-6 as-body-content-top-block as-no-padding-top as-text-right'>
                                {
                                    moduleHasDashboard
                                    &&
                                        <button
                                            className='as-header-redirect-btn'
                                            title='Go To Dashboard'
                                            onClick={() => goToModuleDashboard()}
                                        >
                                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                            <span className="space-left">Dashboard</span>
                                        </button>
                                }
                                {
                                    moduleHasStockReport
                                    &&
                                    <button
                                        className='as-header-redirect-btn'
                                        title='Go To Stock Report'
                                        onClick={() => {
                                            NewWindow(`/inv/report/stock?t=1&url=${true}`)
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faStickyNote} />
                                        <span className="space-left">Stock Details <small>[F1]</small></span>
                                    </button>
                                }

<Menu
                                    menuButton={
                                        <button className='toogle-dropdown as-actions-dropdown-button'>
                                            <FontAwesomeIcon icon={faUserCircle} />
                                            <span className="space-left">{localStorage.full_name}</span>
                                        </button>
                                    }
                                    menuClassName={'as-topbar-menu-list-container'}
                                    align='end'
                                    arrow={true}
                                    transition
                                >
                                    <MenuHeader
                                        className={'as-menu-list-header-info'}
                                    >
                                        <div className='profile-container'>
                                            <div className='icon-container'>
                                                <FontAwesomeIcon icon={faUserTie} className='profile-icon' />
                                            </div>
                                            <div className='profile-details'>
                                                <span className='name'>{localStorage.full_name}</span>
                                            </div>
                                        </div>
                                    </MenuHeader>
                                    <MenuDivider />
                                    {isTouchOrderScreenActive &&
                                        <>
                                            <MenuItem
                                                className={'as-menu-list-item'}
                                                onClick={() => toggleOnScreenKeyboard()}
                                            >
                                                <FontAwesomeIcon icon={faKeyboard} className='menu-icon as-text-light' />
                                                {onScreenKeyboardVisible ? 'Hide' : 'Show'} On-Screen Keyboard
                                            </MenuItem>
                                            <MenuDivider />
                                        </>
                                    }
                                    <MenuItem
                                        className={'as-menu-list-item'}
                                        onClick={() => OpenPropertySwitchModal()}
                                    >
                                        <FontAwesomeIcon icon={faShuffle} className='menu-icon as-text-theme' />
                                        Switch Property
                                    </MenuItem>
                                    <MenuItem
                                        className={'as-menu-list-item'}
                                        onClick={() => OpenPWChangeModal()}
                                    >
                                        <FontAwesomeIcon icon={faLock} className='menu-icon as-text-theme' />
                                        Change Password
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => showQRCode()}                                        
                                        className={'as-menu-list-item'}
                                    >
                                        <FontAwesomeIcon icon={faQrcode} className='menu-icon as-text-theme' />
                                        Show QR
                                    </MenuItem>
                                    {SignatureUploadAllowed &&
                                        <>
                                            <MenuItem
                                                onClick={() => OnSignatureClick()}                                        
                                                className={'as-menu-list-item'}
                                            >
                                                <FontAwesomeIcon icon={faFileSignature} className='menu-icon as-text-theme' />
                                                Upload Signature
                                            </MenuItem>
                                        </>
                                    }

                                    <MenuDivider />
                                    <SubMenu
                                        label={
                                            <>
                                                <FontAwesomeIcon icon={faLanguage} className='menu-icon as-text-theme ml-2' />
                                                Change Language
                                            </>
                                        }
                                        // menuClassName={'as-menu-list-item'}
                                        overflow='auto'
                                    >
                                        {appState?.localization_language?.map((lang, index) => {
                                            const isSelected = localStorage.getItem('language') === lang.short_name
                                            return (
                                                <MenuItem
                                                    key={index}
                                                    onClick={() => handleLanguageChange(lang)}
                                                    className={'as-menu-list-item'}
                                                    style={isSelected ? { backgroundColor: '#e6f7ff'} : {}}
                                                >
                                                <FontAwesomeIcon icon={faLanguage} className='menu-icon as-text-theme' />
                                                {lang.label}
                                            </MenuItem>
                                            );
                                        })}
                                    </SubMenu>
                                    <MenuDivider />
                                    <MenuItem
                                        href="https://knowledgebase.aegis.com.np/shelves/aegis-user-manual"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={'as-menu-list-item'}
                                    >
                                        <FontAwesomeIcon icon={faCircleInfo} className='menu-icon as-text-theme' role='presentation' />
                                        Help
                                    </MenuItem>
                                    <MenuDivider />
                                    {/* <MenuItem
                                        className={'as-menu-list-item'}
                                        onClick={() => LogoutUserAlert(true)}
                                    >
                                        <FontAwesomeIcon icon={faRightFromBracket} className='menu-icon as-text-light' />
                                        Logout & Forget This Device
                                    </MenuItem> */}
                                    <MenuItem
                                        className={'as-menu-list-item'}
                                        onClick={() => LogoutUserAlert(false)}
                                    >
                                        <FontAwesomeIcon icon={faRightFromBracket} className='menu-icon as-text-red' />
                                        Logout User
                                    </MenuItem>
                                    <MenuDivider />
                                    <MenuHeader
                                        className={'as-menu-list-header-info'}
                                    >
                                        <div className='support-info'>
                                            <span className='header'>Support</span>
                                            <div className='support-container'>
                                                <div className='phone-number-container'>
                                                    <span className='phone-number'>970-709-6690</span>
                                                </div>
                                                <div className='icon-container'>
                                                    <FontAwesomeIcon icon={faPhone} />
                                                </div>
                                            </div>
                                            <span className='header email'>Email</span>
                                            <div className='support-container'>
                                                <div className='email-container'>
                                                    <span className='email'>support@aegishms.com</span>
                                                </div>
                                                <div className='icon-container'>
                                                    <FontAwesomeIcon icon={faEnvelope} />
                                                </div>
                                            </div>
                                        </div>
                                    </MenuHeader>
                                </Menu>

                            </div>
                        }
                    </div>
                </div>
                <Suspense fallback={<Loader left="0px" />}>
                    <Switch>
                        {/* APPLICATION SETUP */}
                        <Route path="/app/setup/users" component={User} exact />
                        <Route path="/app/setup/role" component={Roles} exact />
                        <Route path="/app/setup/propertysetup" component={PropertySetup} exact />
                        <Route path="/app/setup/departmentsetup" component={DepartmentSetup} exact />
                        <Route path="/app/setup/costcentersetup" component={CostCenterSetup} exact />
                        <Route path="/app/setup/designationsetup" component={DesignationSetup} exact />
                        <Route path="/app/setup/reasonssetup" component={ReasonsSetup} exact />
                        <Route path="/app/setup/currencysetup" component={CurrencySetup} exact />
                        <Route path="/app/setup/cardtypesetup" component={CardTypeSetup} exact />
                        <Route path="/app/setup/occupationsetup" component={OccupationSetup} exact />
                        <Route path="/app/setup/ReceivableProfileSetup" component={ReceivableProfileSetup} exact />
                        <Route path="/app/setup/ReceivableProfileSubSectionSetup" component={ReceivableProfileSubSectionSetup} exact />
                        <Route path="/app/setup/paymentterm" component={PaymentTermSetup} exact />
                        <Route path="/app/setup/paymentmode" component={PaymentModeSetup} exact />
                        <Route path="/app/setup/exchangerate" component={ExchangeRateSetup} exact />
                        <Route path="/app/setup/PayableProfileSetup" component={PayableProfileSetup} exact />
                        <Route path="/app/setup/modulesettlementsetup" component={ModuleSettlementSetup} exact />
                        <Route path="/app/setup/TaxCodeSetup" component={TaxCodeSetup} exact />
                        <Route path="/app/setup/TaxSetup" component={TaxStructureSetup} exact />
                        <Route path="/app/setup/AreaTypeSetup" component={AreaTypeSetup} exact />
                        <Route path="/app/setup/companytype" component={CompanyTypeSetup} exact />
                        <Route path="/app/setup/FileType" component={FileTypeSetup} exact />
                        <Route path="/app/setup/salesexecutive" component={SalesExecutiveSetup} exact />
                        <Route path="/app/setup/emailtemplate" component={EmailTemplateSetup} exact />
                        <Route path="/app/setup/autoreport" component={AutoReportSetup} exact />
                        <Route path="/app/setup/membershipsetup" component={APPMembership} exact />
                        <Route path="/app/setup/membershipgroupsetup" component={APPMemberGroup} exact />
                        <Route path="/app/setup/GuestStatus" component={APPGuestStatusSetup} exact />
                        <Route path="/app/setup/vouchercode" component={APPVoucherCodeSetup} exact />
                        <Route path="/app/setup/gatepassitemsetup" component={APPGatePassItemSetup} exact /> 
                        <Route path="/app/setup/attendantsetup" component={AttendantSetup} exact /> 
                        <Route path="/app/setup/loyaltylink" component={LoyaltyLink} exact /> 
                        <Route path="/app/tools/message" component={Message} exact /> 
                        <Route path="/app/tools/complaint" component={Complaint} exact /> 
                        <Route path="/app/setup/billinginstruction" component={BillingInstructionSetup} exact />
                        <Route path="/app/setup/businesssource" component={BusinessSourceSetup} exact />
                        <Route path="/app/setup/marketsegment" component={MarketSegmentSource} exact />
                        <Route path="/app/setup/ipsecurity" component={IPSecurity} exact /> 
                        {/* <Route path="/app/setup/transportmode" component={TransportationMode} exact /> 
                        <Route path="/app/setup/driversetup" component={DriverSetup} exact /> 
                        <Route path="/app/setup/transportationpurpose" component={TransportationPurpose} exact />
                        <Route path="/app/setup/transportationstation" component={TransportationStation} exact />  */}
                        <Route path="/app/setup/userrights" component={AppUserRights} exact /> 
                        {/* <Route path="/app/setup/engineeringitem" component={EngineeringItems} exact />  */}
                        <Route path="/app/setup/attendancelink" component={AttendanceLink} exact /> 
                        <Route path="/app/setup/identitytypesetup" component={IdentityTypeSetup} exact /> 
                        <Route path="/app/setup/keymanager" component={KeyManager} exact /> 
                        <Route path="/app/setup/membershipproperty" component={MembershipProperty} exact /> 
                        <Route path="/app/setup/membershiptier" component={MembershipTier} exact /> 
                        <Route path="/app/setup/membershippointconfiguration" component={MembershipPointConfiguration} exact /> 
                        <Route path="/app/setup/incidenttype" component={IncidentType} exact /> 
                        <Route path="/app/tools/barterentry" component={BarterEntry} exact />
                         <Route path="/app/tools/masterimport" component={MasterImport} exact /> 
                        <Route path="/app/setup/clientmanagement" component={ClientManagement} exact /> 
                        <Route path="/app/setup/productname" component={ProductName} exact />
                        <Route path="/app/setup/appsettings" component={AppSettings} exact /> 
                        <Route path="/app/setup/smstemplate" component={SMSTemplate} exact /> 
                        <Route path="/app/setup/BranchSetup" component={BranchSetup} exact />
                        <Route path="/app/setup/BranchLinkSetup" component={BranchLinkingSetup} exact />
                        <Route path="/app/setup/TaxSlabSetup" component={TaxSlabSetup} exact />

                        {/* APPLICATION TOOLS */}
                        <Route path="/app/tools/dayendprocess" component={DayEndProcess} exact />
                        <Route path="/app/tools/backup" component={DatabaseBackup} exact />
                        <Route path="/app/tools/memberpoint" component={MembersPointManagement} exact />
                        <Route path="/app/tools/gatepassissue" component={GatePassIssue} exact />
                        <Route path="/app/tools/gatepassreturn" component={GatePassReturn} exact />
                        <Route path="/app/tools/otpmanagement" component={OTPManagement} exact />
                        {/* <Route path="/app/tools/transportationschedule" component={TransportationSchedule} exact /> */}
                        <Route path="/app/setup/shiftclose" component={ShiftClose} exact /> 
                        <Route path="/app/tools/billheaderupdate" component={BillHeaderUpdate} exact /> 
                        <Route path="/app/tools/incidentreporting" component={IncidentReporting} exact /> 

                        <Route path="/app/tools/annualmaintenancecost" component={AnnualMaintenanceCost} exact /> 
                        <Route path="/app/tools/seeddata" component={SeedData} exact /> 
                        
                        <Route path="/app/tools/superusermenu" component={SuperUser} exact /> 
                        <Route path="/app/tools/supporttool" component={SupportTool} exact /> 
                        <Route path="/app/tools/localization" component={Localization} exact />
                        <Route path="/app/tools/localizationresponse" component={LocalizationResponse} exact />


                        {/* APPLICATION REPORT */}
                        <Route path="/app/report/activitylog" component={APPActivityLogReport} exact />
                        <Route path="/app/report/billprint" component={BillPrintReport} exact />
                        <Route path="/app/report/memberpoint" component={MemberPointsReport} exact />

                        <Route path="/app/report/datadump" component={DataDumpReport} exact />

                        <Route path="/app/report/messagereport" component={MessageReport} exact />
                        <Route path="/app/report/complaintreport" component={ComplaintReport} exact />
                        <Route path="/app/report/exchangeratereport" component={ExchangeRateReport} exact />
                        <Route path="/app/report/moduleandpageaccessrightreport" component={ModuleAndPageAccessRightReport} exact />
                        <Route path="/app/report/shiftclosereport" component={ShiftCloseReport} exact />
                        <Route path="/app/report/gatepassreport" component={GatePassReport} exact />
                        <Route path="/app/report/annualmaintenencecostreport" component={AMCReport} exact />
                        <Route path="/app/report/gatepassreturnreport" component={GatePassReturnReport} exact />


                        {/* FRONT OFFICE SETUP */}
                        <Route path="/fo/setup/roomcategorysetup" component={RoomCategorySetup} exact />
                        <Route path="/fo/setup/RoomFeatureSetup" component={RoomFeatureSetup} exact />
                        <Route path="/fo/setup/RoomSetup" component={RoomSetup} exact />
                        <Route path="/fo/setup/revenuetype" component={RevenueType} exact />
                        <Route path="/fo/setup/revenuecode" component={RevenueCode} exact />
                        <Route path="/fo/setup/UserRight" component={FOUserRight} exact />
                        <Route path="/fo/setup/GuestStatus" component={GuestStatusSetup} exact />
                        <Route path="/fo/setup/ReservationMode" component={ReservationModeSetup} exact />
                        <Route path="/fo/setup/HouseKeeper" component={HousekeeperSetup} exact />
                        <Route path="/fo/setup/packagerate" component={PackageRate} exact />
                        <Route path="/fo/setup/rackrate" component={RackRate} exact />
                        <Route path="/fo/setup/FODefaults" component={FODefault} exact />
                        <Route path="/fo/setup/settings" component={FOSettings} exact />
                        <Route path="/fo/setup/reporttemplate" component={ReportTemplate} exact />
                        <Route path="/fo/setup/channelManagerRate" component={ChannelManageRate} exact />
                        <Route path="/fo/setup/airlinesetup" component={AirlineSetup} exact />
                        <Route path="/fo/setup/flightsetup" component={FlightSetup} exact />
                        <Route path="/fo/setup/ChannelManager" component={ChannelManager} exact />
                        <Route path="/fo/setup/GuestProfile" component={GuestProfile} exact />
                        <Route path="/fo/setup/templatedesign" component={TemplateDesign} exact />
                        {/* <Route path="/fo/setup/RoomAmenities" component={RoomAmenities} exact /> */}
                        <Route path="/fo/setup/reportkeywords" component={ReportKeyword} exact />
                        <Route path="/fo/setup/BlockInventory" component={BlockInventory} exact />
                        <Route path="/fo/setup/MandatoryField" component={MandatoryField} exact />
                        {/* <Route path="/fo/setup/bookingenginelink" component={BookingEngine} exact /> */}
                        {/* <Route path="/fo/setup/amenities" component={Amenities} exact /> */}
                        <Route path="/fo/setup/FeerAccountSetup" component={FeerAccountSetup} exact />
                        <Route path="/fo/setup/FeerExchangeRateSetup" component={FeerExchangeRateSetup} exact />
                        <Route path="/fo/setup/ReportTemplateV2" component={RevenueBudgetV2} exact />
                        <Route path="/fo/setup/BookingChannel" component={BookingChannel} exact />
                        <Route path="/fo/setup/greetingappsetting" component={GreetingAppSetting} exact />
                        <Route path="/fo/setup/reportconfiguration" component={ReportConfiguration} exact />
                        <Route path="/fo/setup/feercurrencysetup" component={FeerCurrencySetup} exact />
                        <Route path="/fo/setup/roommapping" component={RoomMapping} exact />
                        <Route path="/fo/setup/bookingchannelmerchantsetup" component={BookingChannelMerchantSetup} exact />

                        {/* FRONT OFFICE TRANSACTION */}
                        <Route path="/fo/dashboard" component={FODashboard} exact/>
                        <Route path="/fo/transaction/reservation" component={FOReservation} exact/>
                        <Route path="/fo/transaction/reservationv2" component={FOReservationV2} exact/>
                        <Route path="/fo/transaction/checkin" component={FOCheckIn} exact/>
                         <Route path="/fo/transaction/checkinv2" component={FOCheckinV2} exact/>
                        <Route path="/fo/transaction/Receipt" component={FoReceipt} exact />
                        <Route path="/fo/transaction/Payment" component={FoPayment} exact />
                        <Route path="/fo/transaction/additionalrate" component={FoAdditionalRate} exact />
                        <Route path="/fo/transaction/linkdelink" component={FoLinkDeLinkRooms} exact />
                        <Route path="/fo/transaction/changerate" component={ChangeRate} exact />
                        <Route path="/fo/transaction/roomtransferexchange" component={TransferRooms} exact />
                        <Route path="/fo/transaction/stopreleaseposting" component={StopReleasePosting} exact />
                        <Route path="/fo/transaction/roomrateposting" component={RoomRatePosting} exact />
                        <Route path="/fo/transaction/roomratepostingv2" component={RoomRatePostingV2} exact />
                        <Route path="/fo/transaction/roomstatus"  exact render={props=>(<FoRoomStatusUpdate type="FO" menu_id="20_18" title="Room Status |" />)} />         
                        <Route path="/fo/transaction/billsettlement" component={FOBillSettlement} exact />
                        <Route path="/fo/transaction/amendstay" component={AmendStay} exact />
                        <Route path="/fo/transaction/discount" component={Discount} exact />
                        <Route path="/fo/transaction/guestupdate" component={GuestUpdate} exact />
                        <Route path="/fo/transaction/checkout" component={Checkout} exact />
                        <Route path="/fo/transaction/cancelcheckin" component={CancelCheckIn} exact />
                        <Route path="/fo/transaction/billing" component={FOBilling} exact />
                        <Route path="/fo/transaction/creditnote" component={FOCreditNote} exact />
                        <Route path="/fo/transaction/creditnoteprint" component={FOCreditNotePrint} exact />
                        <Route path="/fo/transaction/billprint" component={FOBillRePrint} exact />
                        <Route path="/fo/transaction/fobudget" component={FORevenueBudget} exact />
                        <Route path="/fo/transaction/messageandcomplaints" component={MessageAndComplain} exact />
                        <Route path="/fo/transaction/foreignexchangeencashment" component={FOForeignExchangeEncashment} exact />
                        <Route path="/fo/transaction/guestinfo" component={GuestInfoSearch} exact />
                        <Route path="/fo/transaction/CashBankEntry" component={CashBankEntry} exact />
                        <Route path="/fo/transaction/FOBudgetV2" component={FORevenueBudgetV2} exact />
                        <Route path="/fo/transaction/hotelposition" component={HotelPositionV2} exact />
                        <Route path="/fo/transaction/feercardtransaction" component={FeerCardTransaction} exact />
                        <Route path="/fo/transaction/wakeupevent" component={WakeUpEvent} exact />
                        <Route path="/fo/transaction/guesthistoryimport" component={GuestHistoryImport} exact />

                        {/* FRONT OFFICE REPORT */}
                        <Route path="/fo/report/arrival" component={ArrivalReport} exact />
                        <Route path="/fo/report/departure" component={DepartureReport} exact />
                        <Route path="/fo/report/SalesTaxRegister" exact render={props=>(<FOSalesTaxRegisterReport type="fo" menu_id="21_14" title="Sales Tax Register | FO Report | " />)} />
                        <Route path="/fo/report/MaterializedView" component={FOMaterializedViewReport} exact />
                        <Route path="/fo/report/forecast" component={ForecastReport} exact />
                        <Route path="/fo/report/cashier" component={FOCashierReport} exact />
                        <Route path="/fo/report/dailyflash" component={RoomFlashReport} exact />
                        <Route path="/fo/report/guestledger" component={GLReport} exact />
                        <Route path="/fo/report/nightaudit" component={NightAuditReport} exact />
                        <Route path="/fo/report/ChangeRate" component={ChangeRateReport} exact />
                        <Route path="/fo/report/roomrateoftheday" component={RoomRateOfTheDay} exact />
                        <Route path="/fo/report/VoucherPrint" component={FOVoucherPrint} exact />
                        <Route path="/fo/report/managers" component={FOManagersReport} exact />
                        <Route path="/fo/report/occupancy" component={FOOccupancyReport} exact />
                        <Route path="/fo/report/discountreport" component={FODiscountReport} exact />
                        <Route path="/fo/report/highbalance" component={FOHighBalanceReport} exact />
                        <Route path="/fo/report/revenuematerializedreport" component={FORevenueMateralizedReport} exact />
                        <Route path="/fo/report/ratevariance" component={FORateVarianceReport} exact />
                        <Route path="/fo/report/revenuebudget" component={FORevenueBudgetReport} exact />
                        <Route path="/fo/report/RevenueBudgetReportV2" component={FORevenueBudgetReportV2} exact />
                        <Route path="/fo/report/checkinreport" component={CheckinReport} exact />
                         <Route path="/fo/report/salescontributionreport" exact render={props=>(<SalesContributionReport type="fo" menu_id="21_26" title="Sales Contribution Report | FO Report | " />)} />
                        <Route path="/fo/report/MaterializedAndForecastReport" exact render={props=>(<MaterializedAndForecastReport type="fo" menu_id="21_27" title="Materialized and Forecast Report | FO Report | " />)} />
                        <Route path="/fo/report/Tourism" component={TourismReport} exact />
                        <Route path="/fo/report/reservation" component={ReservationReport} exact />
                        <Route path="/fo/report/GuestMovementReport" component={GuestMovementReport} exact />
                        <Route path="/fo/report/PickUpAndDrop" component={PickUpDropOffReport} exact />
                        <Route path="/fo/report/amendstayreport" component={AmendStayReport} exact />
                        <Route path="/fo/report/RoomAllocationReport" component={RoomAllocationReport} exact />
                        <Route path="/fo/report/RoomStatusLogReport" component={RoomStatusLogReport} exact />
                        <Route path="/fo/report/ComplimentaryReport" component={ComplementaryReport} exact />
                        <Route path="/fo/report/taxcodesummaryreport" exact render={props=>(<TaxCodeSummaryReport menu_id="21_33" title="Tax Code Summary Report | FO Report | " type="fo" />)} />  
                        <Route path="/fo/report/ledgerreport" exact render={props=>(<LedgerReport menu_id="20_3" title="Ledger Report | FO Report | " type="fo" />)} /> 

                        <Route path="/fo/report/MealPlanSummaryReport" component={MealPlanSummaryReport} exact />
                        <Route path="/fo/report/DailyTransactionEntryReport" component={DailyTransactionEntryReport} exact />
                        <Route path="/fo/report/ReceiptDetailReport" component={ReceiptDetailReport} exact />
                        <Route path="/fo/report/ForeignExchangeBalanceReport" component={ForeignExchangeBalanceReport} exact />
                        <Route path="/fo/report/revenueanalysis" component={RevenueAnalysisReport} exact />
                        <Route path="/fo/report/StatementOfForeignCurrency" component={StatementOfForeignCurrency} exact />
                        <Route path="/fo/report/feercardtransactionreport" component={FEERCardTransactionReport} exact />
                        <Route path="/fo/report/feercardtransactionreport" component={FEERCardTransactionReport} exact />
                        <Route path="/fo/report/feercashhandoverreport" component={FEERCashHandoverReport} exact />
                        <Route path="/fo/report/wakeupeventreport" component={WakeUpEventReport} exact />
                        <Route path="/fo/report/buysellreport" component={FEERBuySellReport} exact />
                        <Route path="/fo/report/monthlyfeertransactionreport" component={MonthlyFeerTransactionReport} exact />
                        <Route path="/fo/report/feerprofitlossreport" component={FeerPLReport} exact />
                        <Route path="/fo/report/reservationadvancereport" component={ReservationAdvanceReport} exact />
                        <Route path="/fo/report/dailysalesreport" component={DailySalesReport} exact />
                        <Route path="/fo/report/folioreport" component={FolioReport} exact />
                        <Route path="/fo/report/earlylatearrivalanddeparturereport" component={EarlyLateArrivalDepartureReport} exact />
                        <Route path="/fo/report/vipguestreport" component={VIPGuestReport} exact />
                        <Route path="/fo/report/foliotransferreport" component={FolioTransferReport} exact />
                        <Route path="/fo/report/logreport" component={LogReport} exact />

                        <Route path="/fo/report/analysisreport" component={AnalysisReport} exact />
                        {/* <Route path="/fo/report/logreport" component={LogReport} exact /> */}
                        <Route path="/fo/report/outputregisterreport" exact render={props=>(<OutputRegisterReport type="fo" menu_id="21_55" title="Output Register Report | FO Report | " />)} />
                        <Route path="/fo/report/gstr1" component={Gstr1Report} exact />

                        {/* POINT OF SALE SETUP */}
                        <Route path="/pos/setup/outlet" component={POSOutletSetup} exact />
                        <Route path="/pos/setup/outletsession" component={POSOutletSession} exact />
                        <Route path="/pos/setup/session" component={POSSession} exact />
                        <Route path="/pos/setup/outletordertype" component={POSOutletOrderType} exact />
                        <Route path="/pos/setup/ordertype" component={POSOrderType} exact />
                        <Route path="/pos/setup/kitchen" component={KitchenSetup} exact />
                        <Route path="/pos/setup/outlettable" component={POSOutletTable} exact />
                        <Route path="/pos/setup/openitem" component={OpenItemSetup} exact />
                        <Route path="/pos/setup/outletmenugroup" component={OutletMenuGroup} exact />
                        <Route path="/pos/setup/menugroup" component={MenuGroup} exact />
                        <Route path="/pos/setup/menucategory" component={POSMenuCagetory} exact />
                        <Route path="/pos/setup/menu" component={POSMenuSetup} exact />
                        <Route path="/pos/setup/outletattendant" component={POSOutletAttendant} exact />
                        <Route path="/pos/setup/outletattendantlink" component={POSOutletAttendantLink} exact />
                        <Route path="/pos/setup/userright" component={POSUserRights} exact />
                        <Route path="/pos/setup/outletkotgroup" component={KotGroupAndMenuLink} exact />
                        <Route path="/pos/setup/kotgroup" component={KotGroup} exact />
                        <Route path="/pos/setup/happyhour" component={POSHappyHourSetup} exact />
                        <Route path="/pos/setup/outletncauthority" component={NCAuthorityOutletLink} exact />
                        <Route path="/pos/setup/ncauthoritysetup" component={NCAuthoritySetup} exact />
                        <Route path="/pos/setup/contractratesetup" component={POSContractRateSetup} exact />
                        {/* <Route path="/pos/setup/membershipsetup" component={POSMembership} exact />
                        <Route path="/pos/setup/membershipgroupsetup" component={POSMemberGroup} exact /> */}
                        <Route path="/pos/setup/functiontypesetup" component={POSFunctionType} exact />
                        {/* <Route path="/pos/setup/vouchercode" component={POSVoucherCodeSetup} exact /> */}
                        <Route path="/pos/setup/outletcounter" component={POSOutletCounterSetup} exact />
                        <Route path="/pos/setup/countergroup" component={CounterGroup} exact />
                        <Route path="/pos/setup/counterkitchenmapping" component={CounterKitchenMapping} exact />
                        <Route path="/pos/setup/seatingarrangement" component={SeatingArrangement} exact />
                        <Route path="/pos/setup/templatedesign" component={POSTemplateDesign} exact />
                        <Route path="/pos/setup/ncuser" component={NCUserSetup} exact />
                        <Route path="/pos/setup/ticketmapping" component={TicketMapping} exact/>
                        <Route path="/pos/setup/variant" component={Variant} exact/>
                        <Route path="/pos/setup/varianttype" component={VariantType} exact/>
                        <Route path="/pos/setup/preorderfor" component={PreOrderFor} exact/>
                        <Route path="/pos/setup/preordersettings" component={PreOrderSettings} exact/>
                        {/* POINT OF SALE TRANSACTION */}
                        <Route path="/pos/dashboard" component={POSDashboard} exact />
                        <Route path="/pos/transaction/order" component={POSOrder} exact />
                        <Route path="/pos/transaction/aegistouchorder" component={POSTouchOrder} exact />
                        <Route path="/pos/transaction/bill" component={PosBilling} exact />
                        <Route path="/pos/transaction/settlement" component={POSBillSettlement} exact />
                        <Route path="/pos/transaction/creditnote" component={POSCreditNote} exact />
                        <Route path="/pos/transaction/creditnoteprint" component={POSCreditNotePrint} exact />
                        <Route path="/pos/transaction/billprint" component={POSBillRePrint} exact />
                        <Route path="/pos/transaction/production" component={Production} exact/>
                        <Route path="/pos/transaction/productionv2" component={ProductionV2} exact/>
                        <Route path="/pos/transaction/coverupdate" component={CoverUpdate} exact/>
                        <Route path="/pos/transaction/CityLedger" component={POSCityLedger} exact/>
                        <Route path="/pos/transaction/eventbooking" component={EventBooking} exact/>
                        {/* <Route path="/pos/transaction/ticketmapping" component={TicketMapping} exact/> */}
                        <Route path="/pos/transaction/eventbilltagging" component={EventBillTagging} exact/>
                        <Route path="/pos/transaction/ticketverification" component={TicketVerification} exact />
                        <Route path="/pos/transaction/eventdashboard" component={EventDashboard} exact />
                        <Route path="/pos/transaction/preorder" component={PreOrder} exact />
                        {/* POINT OF SALE REPORT */}
                        <Route path="/pos/report/salestaxregister" exact render={props=>(<FOSalesTaxRegisterReport type="pos" menu_id="31_1" title="Sales Tax Register | POS Report | " />)} />
                        <Route path="/pos/report/materializedreport" component={POSMaterializedViewReport} exact />
                        <Route path="/pos/report/cashierreport" component={POSCashierReport} exact />
                        <Route path="/pos/report/discountreport" component={POSDiscountReport} exact />
                        <Route path="/pos/report/cancellationreport" component={POSCancellationReport} exact />
                        <Route path="/pos/report/ncreport" component={POSNCReport} exact />
                        <Route path="/pos/report/salesbyitem" component={POSSalesByItemReport} exact />
                        <Route path="/pos/report/salessummary" component={POSSalesSummaryReport} exact />
                        <Route path="/pos/report/ticketanalysis" component={POSTicketAnalysisReport} exact />
                        <Route path="/pos/report/eventreservation" component={POSEventReservationReport} exact />
                        <Route path="/pos/report/transactionsummary" component={POSTransactionSummary} exact />
                        <Route path="/pos/report/coveranalysis" component={PosCoverAnalysisReport} exact />
                        <Route path="/pos/report/taxcodesummaryreport" exact render={props=>(<TaxCodeSummaryReport menu_id="31_13" title="Tax Code Summary Report | POS Report | " type="pos" />)} />   
                        <Route path="/pos/report/ledgerreport" exact render={props=>(<LedgerReport menu_id="30_8" title="Ledger Report | POS Report | " type="pos" />)} /> 
                        <Route path="/pos/report/deliveryreport" component={POSDeliveryReport} exact />
                        <Route path="/pos/report/reportprint" component={ReportPrint} exact />
                        <Route path="/pos/report/itemtransfer" component={POSTransferItemReport} exact />
                        <Route path="/pos/report/eventmaterializedandforecast" component={EventMaterializedAndForecast} exact />
                        <Route path="/pos/report/outputregisterreport" exact render={props=>(<OutputRegisterReport type="pos" menu_id="31_20" title="Output Register Report | POS Report | " />)} />
                        <Route path="/pos/report/productionreport" component={POSProductionReport} exact />
                        
                        {/* INVENTORY SETUP */}
                        <Route path="/inv/setup/StoreSetup" component={StoreSetup} exact />
                        <Route path="/inv/setup/StoreItemGroupSetup" component={StoreItemGroupSetup} exact />
                        <Route path="/inv/setup/StoreItemSetup" component={StoreItemSetup} exact />
                        <Route path="/inv/setup/StoreStartPeriod" component={StoreStartPeriod} exact />
                        <Route path="/inv/setup/DateLock" component={StoreLockPeriod} exact />
                        <Route path="/inv/setup/StoreGroupAccessRightSetup" component={StoreGroupAccessRight} exact />
                        <Route path="/inv/setup/UserRights" component={InvUserRights} exact />
                        <Route path="/inv/setup/openingstock" component={OpeningStock} exact />
                        <Route path="/inv/setup/settings" component={INVSettings} exact />
                        <Route path="/inv/setup/vendorcontract" component={VendorContractSetup} exact />
                        <Route path="/inv/setup/AgeingDefinition" component={INVAgeingDefination} exact />
                        <Route path="/inv/setup/physicalstockentry" component={PhysicalStockEntry} exact />
                        <Route path="/inv/setup/locationsetup" component={LocationSetup} exact />



                        {/* INVENTORY TRANSACTION */}
                        <Route path="/inv/dashboard" component={InvDashboard} exact />
                        <Route path="/inv/transaction/StoreRequest" component={StoreRequest} exact />
                        <Route path="/inv/transaction/IssueIndent" component={IssueIndent} exact />
                        <Route path="/inv/transaction/IssueDirect" component={IssueDirect} exact />
                        <Route path="/inv/transaction/IssueReturn" component={IssueReturn} exact />
                        <Route path="/inv/transaction/purchaserequest" component={PurchaseRequest} exact />
                        <Route path="/inv/transaction/PurchaseOrder" component={PurchaseOrder} exact />
                        <Route path="/inv/transaction/receiptreturn" component={ReceiptReturn} exact />
                        <Route path="/inv/transaction/adjustment" component={Adjustment} exact />
                        <Route path="/inv/transaction/Receipt" component={Receipt} exact />
                        <Route path="/inv/transaction/transferitem" component={TransferItem} exact />
                        <Route path="/inv/transaction/posecuritycheck" component={POSecurityCheck} exact />
                        <Route path="/inv/transaction/Receipt" component={Receipt} exact />
                        <Route path="/inv/transaction/transfercostcenter" component={TransferCostCenter} exact />
                        <Route path="/inv/transaction/purge" component={Purge} exact />
                        {/* INVENTORY REPORT */}
                        <Route path="/inv/report/issue" component={IssueReport} exact />
                        <Route path="/inv/report/storerequest" component={StoreRequestReport} exact />
                        <Route path="/inv/report/issuereturn" component={IssueReturnReport} exact />
                        <Route path="/inv/report/storesummary" component={StoreSummaryReport} exact />
                        <Route path="/inv/report/receiptreturnreport" component={ReceiptReturnReport} exact />
                        <Route path="/inv/report/adjustmentreport" component={AdjustmentReport} exact />
                        <Route path="/inv/report/receiptreturn" component={ReceiptReturnReport} exact />
                        <Route path="/inv/report/adjustment" component={AdjustmentReport} exact />
                        <Route path="/inv/report/stock" component={StockReport} exact />
                        <Route path="/inv/report/receipt" component={ReceiptReport} exact />
                        <Route path="/inv/report/purchaserequest" component={PurchaseRequestReport} exact />
                        <Route path="/inv/report/purchaseorder" component={PurchaseOrderReport} exact />
                        <Route path="/inv/report/stockledger" component={StockLedgerReport} exact />
                        <Route path="/inv/report/vatpurchaseregister" exact render={props=>(<PurchaseRegister menu_id="41_1" title="Purchase Register Report | INV Report | " type="inv" />)} />   
                        <Route path="/inv/report/voucherprint" component={INVVoucherPrint} exact />
                        <Route path="/inv/report/ageingreport" component={AgeingDefinationReport} exact />
                        <Route path="/inv/report/expiryitemlist" component={ExpiryItemListReport} exact />
                        <Route path="/ac/report/stockreconcilereport" component={StockReconcileReport} exact />                        
                        <Route path="/inv/report/physicalstockreport" component={PhysicalStockReport} exact />
                        <Route path="/inv/report/transferitemreport" component={TransferItemReport} exact />
                        <Route path="/inv/report/vendorwiseitemanalysisreport" component={VendorWiseItemAnalysisReport} exact />
                        {/* <Route path="/inv/report/receiptreportv2" component={ReceiptReportv2} exact /> receipt report v2 not released for now */} 
                        <Route path="/inv/report/inputregisterreport" exact render={props=>(<InputRegisterReport menu_id="41_21" title="Input Register Report | INV Report | " type="inv" />)} />   

                        {/* ACCOUNT SETUP */}
                        <Route path="/ac/setup/MainHeadSetup" component={MainHeadSetup} exact />
                        <Route path="/ac/setup/SubHeadSetup" component={SubHeadSetup} exact />
                        <Route path="/ac/setup/ChartOfAccountSetup" component={ChartOfAccountSetup} exact />
                        <Route path="/ac/setup/SubLedgerSetup" component={SubLedgerSetup} exact />
                        <Route path="/ac/setup/VoucherTypeSetup" component={VoucherTypeSetup} exact />
                        <Route path="/ac/setup/userright" component={VoucherTypeRight} exact />
                        <Route path="/ac/setup/LinkInventory" component={LinkInventory} exact />
                        <Route path="/ac/setup/StartDate" component={StartDateSetup} exact />
                        <Route path="/ac/setup/DateLock" component={DateLockSetup} exact />
                        <Route path="/ac/setup/LinkAR" component={LinkAR} exact />
                        <Route path="/ac/setup/LinkExmptTax" component={LinkExmptTax} exact />
                        <Route path="/ac/setup/linksales" component={LinkSales} exact />                      
                        <Route path="/ac/setup/openingentry" component={openingBalanceEntry} exact />  
                        <Route path="/ac/setup/settings" component={ACSettings} exact />
                        <Route path="/ac/setup/retainedearning" component={RetainedEarning} exact />
                        <Route path="/ac/setup/AgeingDefination" component={AgeingDefination} exact />
                        <Route path="/ac/setup/templatedesign" component={ACTemplateDesign} exact />
                        <Route path="/ac/setup/reporttemplate" component={ACReportTemplate} exact />
                        <Route path="/ac/setup/linkpayroll" component={LinkPayroll} exact />
                        <Route path="/ac/setup/postinglinksetting" component={PostingLinkSetting} exact />
                        <Route path="/ac/setup/linksalescode" component={LinkSalesCode} exact />
                        
                        {/* ACCOUNT TRANSACTION */}
                        {/* <Route path="/ac/dashboard" component={ACCDashboard} exact /> */}
                        <Route path="/ac/transaction/transactionentry" component={transactionEntry} exact /> 
                        <Route path="/ac/transaction/periodicalexpenses" component={periodicalExpenses} exact /> 
                        <Route path="/ac/transaction/purchaseposting" component={purchasePosting} exact /> 
                        <Route path="/ac/transaction/salesposting" component={salesPosting} exact /> 
                        <Route path="/ac/transaction/statementinvoice" component={StatementInvoice} exact /> 
                        <Route path="/ac/transaction/consumptionposting" component={ConsumptionPosting} exact />
                        <Route path="/ac/transaction/creditnote" component={AcCreditNote} exact />
                        <Route path="/ac/transaction/debitnote" component={AcDebitNote} exact />
                        <Route path="/ac/transaction/billmatching" component={AcBillMatching} exact />
                        <Route path="/ac/transaction/bankreconciliation" component={BankReconciliation} exact />
                        <Route path="/ac/transaction/billtransfer" component={BillTransfer} exact />
                        <Route path="/ac/transaction/billentry" component={billEntry} exact />                        
                        <Route path="/ac/transaction/PLBudget" component={ACPLBudget} exact />
                        <Route path="/ac/transaction/ChequePrinting" component={ChequePrinting} exact />
                        <Route path="/ac/transaction/PayrollPosting" component={ACPayrollPosting} exact />
                        <Route path="/ac/transaction/InvoiceIntegration" component={InvoiceIntegration} exact />                        
                        <Route path="/ac/transaction/debtorsfollowup" component={DebtorsFollowUp} exact />
                        <Route path="/ac/transaction/followupreminder" component={FollowUpReminder} exact />
                        <Route path="/ac/transaction/transactionentryimport" component={TransactionEntryImport} exact />
                         <Route path="/ac/transaction/salesimport" component={SalesImport} exact />
                        <Route path="/ac/transaction/productionposting" component={ProductionPosting} exact />


                        {/* ACCOUNT REPORT */}
                        <Route path="/ac/report/voucherprint" component={ACVoucherPrint} exact />
                        <Route path="/ac/report/TrialBalance" component={TrialBalanceReport} exact />
                        <Route path="/ac/report/PLReport" component={ProfitLossReport} exact />
                        <Route path="/ac/report/ledgerreport" exact render={props=>(<LedgerReport menu_id="51_1" title="Ledger Report | AC Report | " type="ac" />)} />
                        <Route path="/ac/report/AccountLinkModule" component={AccountLinkReport} exact />
                        <Route path="/ac/report/balancesheet" component={BalanceSheetReport} exact />
                        <Route path="/ac/report/remainingposting" component={RemainingPostingReport} exact />
                        <Route path="/ac/report/bankreconciliation" component={BankReconciliationReport} exact />
                        <Route path="/ac/report/ageing" component={AgeingReport} exact />
                        <Route path="/ac/report/cashflowstatement" component={CashFlowStatement} exact />
                        <Route path="/ac/report/transactionchecklist" component={TransactionCheckList} exact />
                        <Route path="/ac/report/settlementreport" component={ACSettlementReport} exact />
                        <Route path="/ac/report/taxreport" component={TaxReport} exact />
                        <Route path="/ac/report/transactionlogreport" component={TransactionLogReport} exact />
                        <Route path="/ac/report/tdsreport" component={TDSReport} exact />                                             
                        {/* <Route path="/ac/report/costreconciliation" component={CostReconciliationReport} exact /> */}
                        <Route path="/ac/report/ledgerreconcilereport" component={LedgerReconcileReport} exact />
                        <Route path="/ac/report/reconcilereport" exact component={ReconcileReport} />
                        <Route path="/ac/report/billwiserevenuereport" exact render={props=>(<BillWiseRevenueReport type="ac" menu_id="51_26" title="Bill Wise Revenue Report | AC Report | " />)} />
                        <Route path="/ac/report/materializedview" exact component={ACMaterializedViewReport} />
                        <Route path="/ac/report/salestaxregister" exact render={props=>(<FOSalesTaxRegisterReport type="ac" menu_id="51_21" title="Sales Tax Register | AC Report | " />)} />
                        <Route path="/ac/report/taxcodesummaryreport" exact render={props=>(<TaxCodeSummaryReport menu_id="51_23" title="Tax Code Summary Report | AC Report | " type="ac" />)} />
                        <Route path="/ac/report/vatpurchaseregister" exact render={props=>(<PurchaseRegister menu_id="51_24" title="Purchase Register Report | AC Report | " type="ac" />)} />   
                        <Route path="/ac/report/debtorsbalancereport" component={DebtorsBalanceReport} exact />
                        <Route path="/ac/report/monthlyvatsubmissionreport" component={MonthlyVATSubmissionReport} exact />
                        <Route path="/ac/report/logreport" component={ACLogReport} exact />
                        <Route path="/ac/report/outputregisterreport" exact render={props=>(<OutputRegisterReport type="ac" menu_id="51_30" title="Output Register Report | AC Report | " />)} />
                        <Route path="/ac/report/debtorsfollowupreport" component={DebtorsFollowUpReport} exact />
                        <Route path="/ac/report/debtorsfollowupcalllogreport" component={DebtorsFollowUpCallLogReport} exact />

                        {/* COSTING SETUP */}
                        <Route path="/cst/setup/recipe" component={ReceipePart} exact />
                        <Route path="/cst/setup/CostExtractionLink" component={CostExtractionLinkingPart} exact />
                        <Route path="/cst/setup/StartDate" component={CSTStartDate} exact />
                        <Route path="/cst/setup/CostReconcileReportSetup" component={CostReconcileReportSetup} />
                        {/* COSTING TRANSACTION */}
                        <Route path="/cst/CostExtraction" component={CostExtraction} exact />
                        <Route path="/cst/EventMenuCosting" component={EventMenuCosting} exact />
                        <Route path="/cst/MenuMix" component={MenuMix} exact />
                         <Route path="/cst/CostExtractionV2" component={CostExtractionV2} exact />
                        {/* COSTING REPORT */}
                        <Route path="/cst/report/recipereport" component={CostRecipeReport} exact />
                        <Route path="/cst/report/costreport" component={CostReport} exact />
                        <Route path="/cst/report/eventmenucostingreport" component={EventMenuCostingReport} exact />
                        <Route path="/cst/report/costreconciliation" component={CostReconciliationReport} exact />
                        <Route path="/cst/report/menumixreport" component={MenuMixReport} exact />

                        
                        {/* HOUSE KEEPING SETUP */}
                        <Route path="/hk/setup/roomstatus" component={RoomStatusSetup} exact />
                        <Route path="/hk/setup/tasktype" component={TaskTypeSetup} exact />
                        <Route path="/hk/setup/amenitiestype" component={AmenitiesType} exact />
                        <Route path="/hk/setup/RoomAmenities" component={RoomAmenities} exact />
                        <Route path="/hk/setup/LaundryItems" component={LaundryItem} exact />
                        <Route path="/hk/setup/hkareasetup" component={HKAreaSetup} exact />
                        {/* HOUSE KEEPING TRANSACTION */}
                        <Route path="/hk/dashboard" component={HKDashboard} exact />
                        <Route path="/hk/transaction/roomstatus" component={HKRoomStatus} exact />
                        <Route path="/hk/transaction/lostandfound" component={HKLostAndFound} exact />
                        <Route path="/hk/transaction/foroomStatus" exact render={props=>(<FoRoomStatusUpdate type="HK" menu_id="70_3" title="Room Status Update" />)} />
                        <Route path="/hk/transaction/amenitiesconsumption" component={LinkAmenities} exact />
                        <Route path="/hk/transaction/housekeepingtask" component={HousekeepingTask} exact />

                        {/* HOUSE KEEPING REPORT */}
                        <Route path="/hk/report/roomstatus" component={HKDiscrepancyReport} exact />
                        <Route path="/hk/report/lostandfoundreport" component={LostAndFoundReport} exact />
                        <Route path="/hk/report/amenitiesconsumptionreport" component={AmenitiesConsumptionReport} exact />

                        {/* HR */}
                        {/* HR Setup*/}
                        
                        <Route path="/hr/setup/staffprofile" component={StaffProfile} exact />
                        <Route path="/hr/setup/PaySlip" component={PaySlip} exact />
                        <Route path="/hr/setup/Payrollhead" component={PayrollHead} exact />
                        <Route path="/hr/setup/templatedesign" component={HRTemplateDesign} exact />
                        <Route path="/hr/setup/staffclassification" component={StaffClassification} exact />
                        <Route path="/hr/setup/userrights" component={HRUserRights} exact />
                        <Route path="/hr/setup/loantype" component={LoanTypeSetup} exact />
                        <Route path="/hr/setup/loanpaymenttype" component={LoanPaymentTypeSetup} exact />
                        <Route path="/hr/setup/settings" component={HRSettings} exact />

                        {/*HR Transaction*/}
                        <Route path="/hr/transaction/payrollposting" component={PayrollPosting} exact />
                        <Route path="/hr/transaction/loanapplyform" component={LoanApplyForm} exact />
                        <Route path="/hr/transaction/loanrepayment" component={LoanRepayment} exact />

                        {/* HR Report */}
                        <Route path="/hr/report/staffpayrollhistory" component={StaffPayrollHistoryReport} exact />
                        <Route path="/hr/report/payslipstructure" component={PayslipStructureReport} exact />
                        <Route path="/hr/report/bankstatement" component={BankStatementReport} exact />
                        <Route path="/hr/report/payrolltransaction" component={PayrollTransactionReport} exact />
                        <Route path="/hr/report/hrtaxreport" component={HRTaxReport} exact />
                        <Route path="/hr/report/staffprofiledetailreport" component={StaffProfileDetailReport} exact />
                        <Route path="/hr/report/staffattendancereport" component={StaffAttendanceReport} exact />
                        <Route path="/hr/report/payslipvspostedreport" component={PayslipvsPostedReport} exact />
                        <Route path="/hr/report/loanreport" component={LoanReport} exact />




                        {/* Booking Engine */}
                        {/* BE Setup */}
                        <Route path="/be/setup/configuration" component={BEConfiguration} exact />
                        <Route path="/be/setup/roomratedetail" component={BERoomRateDetails} exact />
                        <Route path="/be/setup/amenity" component={BEAmenities} exact />
                        <Route path="/be/setup/rate" component={BERate} exact />
                        
                        {/* BE Transaction */}

                        <Route path="/tel/setup/extensionmapping" component={ExtensionMapping} exact />
                        <Route path="/tel/setup/slabrateconfiguration" component={SlabRateConfiguration} exact />
                        <Route path="/tel/setup/countrygroup" component={TELCountryGroup} exact />
                        <Route path="/tel/setup/callidentifier" component={TELCallIdentifier} exact />
                        <Route path="/tel/setup/settings" component={TELSettings} exact />

                        {/* TEL Transaction */}
                        <Route path="/tel/transaction/extensionpermission" component={ExtensionPermission} exact />

                        {/* TEL Report */}
                        <Route path="/tel/report/calllogreport" component={CallLogReport} exact />


                          {/* SNM Transaction*/}
                          <Route path="/snm/transaction/task" component={Task} exact />
                        <Route path="/snm/transaction/calllog" component={CallLog} exact />

                        {/* SNM Setup */}
                        <Route path="/snm/setup/Contact" component={ContactSetup} exact />
                        <Route path="/snm/setup/userrights" component={SNMUserRight} exact />
                        <Route path="/snm/setup/callstatus" component={CallStatusSetup} exact />
                        <Route path="/snm/setup/pipeline" component={PipelineSetup} exact />
                        <Route path="/snm/setup/dealfor" component={DealForSetup} exact />

                        {/* SNM Transaction */}
                        <Route path="/snm/transaction/lead" component={LeadTransaction} exact />

                        {/* SNM Report */}
                        <Route path="/snm/report/forecast" exact render={props=>(<ForecastReport type="snm" menu_id="111_5" title="Forecast Report | SNM Report | " />)} />
                        <Route path="/snm/report/materializedandforecastreport" exact render={props=>(<MaterializedAndForecastReport type="snm" menu_id="111_6" title="Materialized and Forecast Report | SNM Report | " />)} />
                        <Route path="/snm/report/salescontributionreport" exact render={props=>(<SalesContributionReport type="snm" menu_id="111_7" title="Sales Contribution Report | SNM Report | " />)} />
                        <Route path="/snm/report/leadandactivitylogreport" component={LeadAndActivityLogReport} exact />

                        
                        {/* FA Setup */}
                        <Route path="/fa/setup/assetstype" component={AssetsType} exact />
                        <Route path="/fa/setup/assetmanagement" component={AssetMangement} exact />


                        {/* FA Report */}
                        <Route path="/fa/report/fixedassetsregister" component={FixedAssetRegister} exact />
                       


                        {/* OTHER */}
                        <Route path="/design/dashboard" component={DesignDashboard} />
                        <Route path="/template" component={Template} />
                        <Route path="/dummy-api-request" component={DummyApiRequest} />
                        <Route path="/vertical-tabs" component={VerticalTabsDemo} />
                        <Route path="/horizontal-tabs" component={HorizontalTabsDemo} />
                        <Route path="/modal-stack" component={ModalStackDemo} />
                        <Route path="/context-menu" component={ContextMenuDemo} />
                        <Route path="/internal-component" component={InternalComponentAccessDemo} />
                        <Route path="/report" component={Report} />
                        <Route path="/dashboard" component={DesignDashboard} exact />
                        <Route path="/" component={DesignDashboard} exact />
                        <Route path="/authorizationfailure" component={AuthorizationFailureScreen} exact />
                        <Route path="/" component={PageNotFound} />

                    </Switch>
                </Suspense>
                {modal}
            </div>

            { 
                ((license)=>{
                    if(license==null) return <></>;
                    if(license.days>7) return <></>;

                    return <div className='as-watermark-wrapper'>
                            <div className='as-watermark-container'>
                                {license.days==1?
                                    <span className='as-watermark'>Your license is applicable till today.</span>
                                    :
                                    <span className='as-watermark'>Your license will expire in {(parseInt(license.days))} days.</span>
                                }
                                
                            </div>
                        </div> 
                })(license)
                
            }

            <div className='as-page-footer' id='as-page-footer'>
                <div className='row'>
                    <div className='as-form-wrapper'>
                        <div className='col-6 col-m-6 col-s-12 col-x-12'>
                            <span className='as-highlighted-text'>Aegis{product_name} {product_version_name + " "}</span>
                            Licensed To: <span className='as-highlighted-text'>{property_name} {multi_property_code==''?'':' | '} {multi_property_code}</span>,
                            Valid Till: <span className='as-highlighted-text'>{license!=null?ToDateString(license.validity_period):""}</span>.
                        </div>
                        <div className='col-6 col-m-6 col-s-12 col-x-12 as-text-right'>
                            Copyright &copy; 2022, <span className='as-highlighted-text'> AEGIS SOFTWARE PVT. LTD.</span> All Rights Reserved.
                        </div>
                    </div>
                </div>
            </div>


        </>
    );
}

export default AppRoute;